import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { DatePicker } from 'rsuite';
import { Eye, ChevronDown, X, User, DollarSign, Plus, Link2, Check, History, FileStack, Pencil, RotateCcw, Info, GripVertical, Tag, CreditCard } from 'lucide-react';
import TipTapEditor from './TipTapEditor';
import { useAuth } from '../context/AuthContext';
import {
    cotizacionesAPI,
    tiposServicioAPI,
    personalAPI,
    configuracionAPI,
    costosFijosAPI,
    plantillasAPI,
    clientesAPI,
    usuariosAPI,
    categoriasAPI,
    prefijosAPI,
    periodosServicioAPI,
    planesPagoAPI,
    logsAPI
} from '../services/api';
import {
    getCachedTiposServicio,
    getCachedTiposServicioGrouped,
    getCachedPersonal,
    getCachedCostosFijos,
    getCachedCostosFijosGrouped,
    invalidateCostosFijos,
    getCachedPlantillas,
    getCachedClientes,
    getCachedUsuarios,
    getCachedCategorias,
    invalidateTiposServicio,
    invalidateClientes
} from '../services/cachedCatalogs';
import { moneyFmt, safeId, getCurrentDate, totalConRentabilidad, gananciaConRentabilidad, rowTotal, rowGanancia, rowTotalConVariaciones, rowGananciaConVariaciones, aplicarVariaciones, aplicarOferta, etiquetaOferta, calcularMontosPlanPago, hasOverride, formatDateTimeShort, getEstadoColor, nombreArchivoCotizacion } from '../utils/helpers';
import VariacionesModal from './VariacionesModal';
import OfertaModal from './OfertaModal';
import { TrendingDown, TrendingUp, Percent } from 'lucide-react';
import { generatePdfCotizacion } from '../utils/pdfGenerator';
import { selectStyles } from '../utils/adminShared';
import { Modal, Button, Card, Input, EmptyState, Tooltip, useConfirm, PhoneInput } from './ui';
import { useCotizacionDraft } from '../hooks/useCotizacionDraft';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCotizacionLock } from '../hooks/useCotizacionLock';
import HiloVersionesModal from './HiloVersionesModal';
import ClienteAcciones from './ClienteAcciones';
import AnexosSection from './AnexosSection';
import AccesoDenegado from './AccesoDenegado';

/**
 * Traduce errores tecnicos (Failed to fetch, 500, 401, etc.) a mensajes en
 * primera persona, en castellano, que le dicen al usuario que paso y que hacer.
 * El `fallback` se usa cuando el error no calza con ninguna heuristica conocida.
 */
const friendlyErrorMessage = (err, fallback) => {
    const msg = err?.message || '';
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'Sin conexión a internet. Revisa tu red e intenta de nuevo.';
    }
    if (/failed to fetch|networkerror|network request failed/i.test(msg)) {
        return 'No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.';
    }
    if (msg.includes('No tienes acceso')) return msg;
    if (/\b401\b|unauthorized/i.test(msg)) {
        return 'Tu sesión expiró. Vuelve a iniciar sesión para continuar.';
    }
    if (/\b403\b|forbidden/i.test(msg)) {
        return 'No tienes permisos para esta acción.';
    }
    if (/\b5\d\d\b/.test(msg)) {
        return 'El servidor tuvo un problema. Intenta de nuevo en un minuto.';
    }
    return fallback;
};

/**
 * Skeleton de carga — reflecta la estructura final del formulario (form-top-row +
 * Cards apilados) para que el usuario perciba "el form se esta llenando" en vez
 * de "algo esta cargando, ¿se rompio?". Respeta prefers-reduced-motion: con motion
 * desactivado se muestra estatico con opacidad reducida, sin pulse.
 */
const CotizacionFormSkeleton = () => (
    <div
        role="status"
        aria-label="Cargando cotización"
        aria-live="polite"
        className="motion-safe:animate-pulse motion-reduce:opacity-70"
    >
        <div className="form-top-row">
            <Card>
                <Card.Header>
                    <div className="h-5 w-32 rounded bg-line/60" />
                </Card.Header>
                <Card.Body>
                    <div className="h-10 rounded bg-line/40" />
                    <div className="h-3 w-3/4 rounded bg-line/30 mt-3" />
                </Card.Body>
            </Card>
            <Card className="moneda-card">
                <Card.Header>
                    <div className="h-5 w-44 rounded bg-line/60" />
                </Card.Header>
                <Card.Body className="flex-1 flex flex-col">
                    <div className="flex gap-2.5">
                        <div className="h-10 flex-1 rounded bg-line/40" />
                        <div className="h-10 flex-1 rounded bg-line/40" />
                    </div>
                    <div className="flex gap-2 mt-auto pt-4 justify-center">
                        <div className="h-5 w-32 rounded bg-line/40" />
                        <div className="h-5 w-20 rounded bg-line/40" />
                    </div>
                </Card.Body>
            </Card>
        </div>

        <Card className="mt-3.5">
            <Card.Header>
                <div className="h-5 w-56 rounded bg-line/60" />
            </Card.Header>
            <Card.Body>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                    <div className="space-y-1.5">
                        <div className="h-3 w-32 rounded bg-line/40" />
                        <div className="h-10 rounded bg-line/40" />
                    </div>
                    <div className="space-y-1.5">
                        <div className="h-3 w-40 rounded bg-line/40" />
                        <div className="h-10 rounded bg-line/40" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="space-y-1.5">
                            <div className="h-3 w-20 rounded bg-line/40" />
                            <div className="h-10 rounded bg-line/40" />
                        </div>
                    ))}
                </div>
            </Card.Body>
        </Card>

        <Card className="mt-3.5">
            <Card.Header actions={<div className="h-10 w-40 rounded bg-line/40" />}>
                <div className="h-5 w-24 rounded bg-line/60" />
            </Card.Header>
            <Card.Body>
                <div className="rounded-xl border border-line p-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="h-5 w-8 rounded bg-line/40 shrink-0" />
                            <div className="h-10 flex-1 max-w-sm rounded bg-line/40" />
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-6 w-36 rounded bg-line/40" />
                            <div className="h-7 w-7 rounded bg-line/40" />
                            <div className="h-7 w-7 rounded bg-line/40" />
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </div>
);

/**
 * Total editable de una fila (personal o costo fijo) con override transparente.
 *
 *   - Modo normal: muestra el total efectivo (override si existe, sino calculado).
 *     Click en el lapiz -> entra a modo edicion.
 *     Si tiene override, muestra badge "ajustado" + boton revertir.
 *   - Modo edicion: input numerico inline + boton confirmar/cancelar.
 *     Confirmar con valor vacio o igual al calculado -> revierte (override=null).
 *
 * Props:
 *   total           number   -> total efectivo a mostrar (override || calculado)
 *   calculado       number   -> total puro de la formula (para comparar)
 *   override        number?  -> valor de override actual (null si no tiene)
 *   moneda          string   -> 'PEN' | 'USD'
 *   canEdit         boolean  -> si false, solo muestra valor (sin lapiz ni input)
 *   onChange        fn       -> (nuevoOverride | null) cuando el user confirma
 *   blurMoney       fn       -> (valor, moneda) -> string. Usa el wrapper que respeta ver_detalles
 */
const EditableTotal = ({ total, calculado, override, moneda, canEdit, onChange, blurMoney }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const tieneOverride = hasOverride(override);

    const startEdit = () => {
        setDraft(tieneOverride ? String(override) : String((calculado || 0).toFixed(2)));
        setEditing(true);
    };
    const cancel = () => { setEditing(false); setDraft(''); };
    const confirm = () => {
        const raw = String(draft).trim();
        if (raw === '') {
            onChange(null);
        } else {
            const num = parseFloat(raw);
            if (!Number.isFinite(num) || num < 0) {
                cancel();
                return;
            }
            // Si coincide con el calculado, mejor revertir para mantener fila limpia
            const calc = parseFloat((calculado || 0).toFixed(2));
            const next = parseFloat(num.toFixed(2));
            onChange(next === calc ? null : next);
        }
        setEditing(false);
        setDraft('');
    };

    if (!canEdit) {
        return <span className="total-value">{blurMoney(total, moneda)}</span>;
    }

    if (editing) {
        return (
            <span className="total-edit-inline">
                <input
                    className="input input-sm total-edit-input"
                    type="number"
                    min="0"
                    step="0.01"
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); confirm(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                    }}
                    onBlur={confirm}
                />
            </span>
        );
    }

    return (
        <span className={`total-display ${tieneOverride ? 'has-override' : ''}`}>
            {tieneOverride ? (
                <Tooltip content={`Total manual. Calculado: ${moneyFmt(calculado, moneda)}`}>
                    <span className="total-value">{blurMoney(total, moneda)}</span>
                </Tooltip>
            ) : (
                <span className="total-value">{blurMoney(total, moneda)}</span>
            )}
            <span className="total-actions">
                {tieneOverride && (
                    <Tooltip content={`Revertir al calculado (${moneyFmt(calculado, moneda)})`}>
                        <button
                            type="button"
                            className="total-revert-btn"
                            onClick={() => onChange(null)}
                            aria-label="Revertir al total calculado"
                        >
                            <RotateCcw size={12} />
                        </button>
                    </Tooltip>
                )}
                <Tooltip content="Editar total (legacy)">
                    <button
                        type="button"
                        className="total-edit-btn"
                        onClick={startEdit}
                        aria-label="Editar total"
                    >
                        <Pencil size={12} />
                    </button>
                </Tooltip>
            </span>
        </span>
    );
};

/**
 * Badge/boton para la columna VARIACION. Se coloca en su propia columna, no
 * mezclado con el total. Muestra:
 *   - Icono TrendingDown (descuento), TrendingUp (extra), Percent (mixto)
 *   - Numero de variaciones aplicadas (si > 0)
 *   - Colores: rojo/verde/primary segun efecto neto
 * Click → abre modal de variaciones.
 */
const VariacionesBadge = ({ variaciones, canEdit, onOpen }) => {
    const count = Array.isArray(variaciones) ? variaciones.length : 0;
    const netEffect = (() => {
        if (count === 0) return 'none';
        const sumaExtras = variaciones.filter(v => v.tipo === 'extra').length;
        const sumaDesc = variaciones.filter(v => v.tipo === 'descuento').length;
        if (sumaExtras > 0 && sumaDesc > 0) return 'mixto';
        return sumaDesc > 0 ? 'descuento' : 'extra';
    })();
    const btnClass = count > 0
        ? `variacion-fila-btn has-variaciones effect-${netEffect}`
        : 'variacion-fila-btn';
    const iconMap = { descuento: TrendingDown, extra: TrendingUp, mixto: Percent, none: Percent };
    const Icon = iconMap[netEffect];

    if (!canEdit) return null;

    return (
        <Tooltip content={count > 0 ? `${count} variación${count === 1 ? '' : 'es'} de precio aplicada${count === 1 ? '' : 's'}` : 'Aplicar variación de precio'}>
            <button
                type="button"
                className={btnClass}
                onClick={onOpen}
                aria-label="Aplicar variación de precio"
            >
                <Icon size={11} />
                {count > 0 && <span>{count}</span>}
            </button>
        </Tooltip>
    );
};

/**
 * Total de fila SIMPLE (sin badge integrado). El badge vive ahora en su propia
 * columna via `<VariacionesBadge>`. Este componente solo muestra el numero
 * con blur + indicador de override legacy.
 */
const RowVariacionesTotal = ({ total, hasLegacyOverride, moneda, blurMoney }) => (
    <span className="total-display">
        <span className="total-value">
            {blurMoney(total, moneda)}
            {hasLegacyOverride && (
                <Tooltip content="Esta fila tiene un ajuste manual heredado. Se migrará a variación al guardar.">
                    <span style={{ marginLeft: 4, color: 'rgb(var(--warning))', fontSize: 11 }}>•</span>
                </Tooltip>
            )}
        </span>
    </span>
);

const CotizacionForm = ({
    cotizacionId = null,
    tipoCambio: tipoCambioGlobal,
    igvRate,
    rentabilidad = 0.68,
    rentabilidadCostoFijo,
    configuracion,
    aiData = null,
    onSaved,
    onCancel
}) => {
    const navigate = useNavigate();
    // Obtener usuario logueado
    const { user, tienePermiso } = useAuth();
    const verDetalles = tienePermiso('ver_detalles_costos');
    const priceClass = verDetalles ? '' : 'price-hidden';
    const blurMoney = (value, moneda) => verDetalles ? moneyFmt(value, moneda) : '•••••';
    const confirm = useConfirm();

    // Usar valor de prop o default si no se pasa (null/undefined)
    const rentCostoFijo = rentabilidadCostoFijo !== undefined && rentabilidadCostoFijo !== null
        ? rentabilidadCostoFijo
        : 0.68;
    // Estados
    const [loading, setLoading] = useState(false);
    // Se activa al detectar 403 desde el backend. Corta el render y evita que
    // los sub-efectos (lock, anexos, etc.) sigan disparando requests.
    const [accesoDenegado, setAccesoDenegado] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tiposServicio, setTiposServicio] = useState([]); // Lista plana para compatibilidad
    const [tiposServicioGrouped, setTiposServicioGrouped] = useState([]); // Agrupados para react-select
    const [personalList, setPersonalList] = useState([]);
    const [costosFijosList, setCostosFijosList] = useState([]); // Lista plana de costos fijos
    const [costosFijosGrouped, setCostosFijosGrouped] = useState([]); // Agrupados por categoría
    const [numeroCotizacion, setNumeroCotizacion] = useState('');
    // Switch "esta cotizacion tambien tiene un PDF manual adjunto".
    // NO cambia es_manual en BD (la cotizacion sigue normal con servicios/personal).
    // Solo desbloquea el uploader y el archivo se renombra a {numero}-MANUAL.{ext}.
    const [adjuntarArchivo, setAdjuntarArchivo] = useState(false);
    const [archivo, setArchivo] = useState(null); // nuevo file seleccionado
    const [archivoActual, setArchivoActual] = useState(null); // {archivo_path, archivo_nombre} desde BD
    const [dragArchivo, setDragArchivo] = useState(false);
    // Prefijos activos disponibles y el elegido al crear una cotizacion nueva.
    // Solo se muestra el selector cuando hay 2+ prefijos activos.
    const [prefijosActivos, setPrefijosActivos] = useState([]);
    const [prefijoSeleccionado, setPrefijoSeleccionado] = useState(null);
    // Periodos activos (mensual, anual, etc) para el switch por servicio.
    const [periodosActivos, setPeriodosActivos] = useState([]);
    // Planes de pago activos (opcional al final del form; interno)
    const [planesPagoActivos, setPlanesPagoActivos] = useState([]);
    const [planPagoId, setPlanPagoId] = useState(null);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null); // { por: string, at: ISO } en modo edicion
    // TC guardado en la cotizacion (persistente). null = usar el TC global de configuracion.
    // Se setea al cargar una cotizacion existente que tiene tipo_cambio guardado.
    // Reglas del TC efectivo (calculado mas abajo, despues de declarar `cotizacion`):
    //   - Cotizacion nueva o en borrador → TC global (se actualiza diariamente).
    //   - Cotizacion enviada / aprobada / rechazada → TC congelado al pasar de
    //     borrador (preserva el TC que vio el cliente cuando se envio).
    const [tcCotizacionGuardado, setTcCotizacionGuardado] = useState(null);
    const [copiadoLink, setCopiadoLink] = useState(false); // feedback transitorio del boton "Copiar link"
    const [versionesCount, setVersionesCount] = useState(0); // cantidad de cotis en el hilo (badge)
    const [showHiloModal, setShowHiloModal] = useState(false);
    // Modal de historial de estados: abierto + lista cargada
    const [historialEstados, setHistorialEstados] = useState({ open: false, loading: false, items: [] });
    const [categoriasList, setCategoriasList] = useState([]); // categorias activas para crear nuevo servicio desde el modal
    // Modal "crear servicio rapido" desde el select de servicio.
    // servicioRowId: el _id de la fila de servicio donde se selecciona el nuevo tipo creado.
    const [modalCrearServicio, setModalCrearServicio] = useState({
        open: false,
        servicioRowId: null,
        nombre: '',
        categoria_id: null,
        guardando: false
    });
    const [plantillasList, setPlantillasList] = useState([]); // Lista de plantillas activas
    const [clientesList, setClientesList] = useState([]); // Lista de clientes
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null); // Cliente seleccionado en el select
    const [showClienteModal, setShowClienteModal] = useState(false); // Modal para ver datos del cliente
    const [usuariosList, setUsuariosList] = useState([]); // Lista de usuarios para asignar

    // Datos de la cotizacion
    const [cotizacion, setCotizacion] = useState({
        cliente_id: null,
        cliente_nombre: '',
        cliente_email: '',
        cliente_telefono: '',
        cliente_empresa: '',
        tiempo_desarrollo: '',
        fecha: getCurrentDate(),
        validez: 15,
        moneda: 'PEN',
        incluye_igv: true,
        comentarios: '',
        asignados_ids: []
    });

    // TC efectivo: depende del estado actual de la cotizacion.
    //   - borrador o nueva → global vigente (se actualiza diariamente).
    //   - enviada / aprobada / rechazada → congelado (el guardado en BD).
    const esBorrador = !cotizacion.estado || cotizacion.estado === 'borrador';
    const tipoCambio = esBorrador
        ? tipoCambioGlobal
        : (tcCotizacionGuardado != null && tcCotizacionGuardado !== ''
            ? parseFloat(tcCotizacionGuardado)
            : tipoCambioGlobal);

    // Items de servicio (cada uno con array de asignaciones)
    const [servicios, setServicios] = useState([]);

    // Modal de variaciones de precio. Al abrirse guarda todo el contexto necesario
    // para saber a que fila afectar cuando se aplique cambios (scope, servicio, id).
    // { open: bool, scope: 'asignacion'|'costo_fijo', servicioLocalId, filaLocalId,
    //   base: number, variaciones: array, titulo: string }
    const [variacionesModal, setVariacionesModal] = useState({ open: false });

    // Modal de ofertas comerciales (descuento visible al cliente).
    // { open, scope: 'servicio'|'costo_fijo', servicioLocalId, cfLocalId,
    //   precioBase: number, oferta: obj|null, titulo: string }
    const [ofertaModal, setOfertaModal] = useState({ open: false });

    // Aplica una oferta (o la quita si se pasa null) al servicio/CF actual.
    const aplicarOfertaAScope = (nuevaOferta) => {
        const { scope, servicioLocalId, cfLocalId, asigLocalId } = ofertaModal;
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioLocalId) return s;
            if (scope === 'servicio') {
                return { ...s, oferta: nuevaOferta };
            }
            if (scope === 'costo_fijo') {
                return {
                    ...s,
                    costosFijos: (s.costosFijos || []).map(cf =>
                        cf._id === cfLocalId ? { ...cf, oferta: nuevaOferta } : cf
                    )
                };
            }
            if (scope === 'asignacion') {
                return {
                    ...s,
                    asignaciones: (s.asignaciones || []).map(a =>
                        a._id === asigLocalId ? { ...a, oferta: nuevaOferta } : a
                    )
                };
            }
            return s;
        }));
    };

    // Aplicar los cambios de variaciones al state segun el scope.
    // Se llama desde onChange del modal. No hace request al backend — todo queda
    // en memoria hasta que se guarda la cotizacion.
    const aplicarCambiosVariaciones = (nuevoArray) => {
        const { scope, servicioLocalId, filaLocalId } = variacionesModal;
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioLocalId) return s;
            if (scope === 'asignacion') {
                return {
                    ...s,
                    asignaciones: (s.asignaciones || []).map(a =>
                        a._id === filaLocalId ? { ...a, variaciones: nuevoArray } : a
                    )
                };
            }
            if (scope === 'costo_fijo') {
                return {
                    ...s,
                    costosFijos: (s.costosFijos || []).map(cf =>
                        cf._id === filaLocalId ? { ...cf, variaciones: nuevoArray } : cf
                    )
                };
            }
            if (scope === 'bloque_personal') {
                return { ...s, variaciones_bloque_personal: nuevoArray };
            }
            if (scope === 'bloque_costos') {
                return { ...s, variaciones_bloque_costos: nuevoArray };
            }
            if (scope === 'servicio') {
                return { ...s, variaciones: nuevoArray };
            }
            return s;
        }));
        // Actualizar el propio state del modal para reflejar en tiempo real
        setVariacionesModal(prev => ({ ...prev, variaciones: nuevoArray }));
    };

    // Acordeon: ID del servicio expandido (null = ninguno, pero el primero se abre por defecto)
    const [servicioExpandido, setServicioExpandido] = useState(null);

    // Reordenar servicios arrastrando. draggable se activa solo al presionar el handle:
    // el header tiene selects y botones, y dejarlo siempre activo rompe la seleccion
    // de texto de los inputs y del editor
    const [servicioArmado, setServicioArmado] = useState(null);
    const [servicioArrastrado, setServicioArrastrado] = useState(null);
    const [zonaDestino, setZonaDestino] = useState(null); // { seccion, index }

    // Items extras
    const [extras, setExtras] = useState([]);

    // Autosave de borrador (solo para cotizaciones NUEVAS).
    // Snapshot debounced a localStorage cada ~2s; banner de recuperacion al mount.
    const draft = useCotizacionDraft({ cotizacionId, cotizacion, servicios, extras });

    // Title del tab del browser: "COT-NNN · Cliente" en edicion, "Nueva cotizacion" si no.
    // numeroCotizacion ya viene con el prefijo "COT-" desde la BD.
    useDocumentTitle(
        cotizacionId
            ? `${numeroCotizacion}${cotizacion.cliente_nombre ? ` · ${cotizacion.cliente_nombre}` : ''}`
            : 'Nueva cotización'
    );

    // Modal "perdiste el control" cuando un admin fuerza toma de tu lock.
    // El hook dispara onLockLost al detectar 409 despues de haber tenido el lock,
    // y ya cancelo TODOS los timers (no siguen martillando el server).
    const [lockPerdido, setLockPerdido] = useState(null);

    // Lock pesimista: tomamos el lock cuando hay cotizacionId (modo edicion).
    // Si esta bloqueado por otro, redirigimos a /detalle.
    // Si alguien solicita ceder, mostramos modal bloqueante.
    // Si alguien fuerza toma de nuestro lock, el hook corta timers y llama onLockLost.
    const {
        bloqueadoPor,
        solicitud,
        liberar: liberarLock,
        rechazarSolicitud: rechazarSolicitudLock,
        refrescar: refrescarLock
    } = useCotizacionLock(cotizacionId, {
        modo: 'editor',
        // Solo tomar lock si el usuario tiene acceso — evita 403 en cascada
        // cuando entra a una cotizacion que no le corresponde.
        enabled: !!cotizacionId && !accesoDenegado,
        onLockLost: (nuevoOwner) => {
            setLockPerdido(nuevoOwner);
            toast.error(`${nuevoOwner?.usuario_nombre || 'Otro usuario'} tomó control de esta cotización`, { duration: 5000 });
        }
    });

    // Si vino 409 al TOMAR el lock por primera vez (otro user ya lo tenia),
    // redirigimos a /detalle. Pero si el 409 vino DURANTE el heartbeat (perdimos
    // el lock porque alguien lo forzo), no redirigimos: el modal "Perdiste control"
    // se encarga de darle opciones al user.
    useEffect(() => {
        if (bloqueadoPor && cotizacionId && !lockPerdido) {
            navigate(`/cotizaciones/${cotizacionId}/detalle`, {
                replace: true,
                state: { bloqueadoPor }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bloqueadoPor, lockPerdido]);

    const handleCederLock = async () => {
        await liberarLock();
        // Tras ceder, vamos a /detalle. El solicitante podra tomar el lock alli.
        navigate(`/cotizaciones/${cotizacionId}/detalle`, { replace: true });
    };

    // Al rechazar la solicitud, ademas de limpiar el backend guardamos el
    // timestamp localmente para que aunque el heartbeat siguiente devuelva
    // algo viejo, no se vuelva a abrir el modal.
    const [solicitudIgnoradaAt, setSolicitudIgnoradaAt] = useState(null);
    const handleMantenerLock = async () => {
        if (solicitud?.solicitud_at) setSolicitudIgnoradaAt(solicitud.solicitud_at);
        // Limpiamos en backend para que el solicitante reciba toast "rechazada"
        // en su proximo polling (max 10s).
        await rechazarSolicitudLock();
    };
    const solicitudVisible = solicitud && solicitud.solicitud_at !== solicitudIgnoradaAt;

    // Funcion para convertir costo segun moneda
    // Los costos en BD estan en PEN, si la moneda es USD dividimos por tipo de cambio
    const convertirCosto = useCallback((costoPEN) => {
        const costo = parseFloat(costoPEN) || 0;
        if (cotizacion.moneda === 'USD') {
            return costo / (parseFloat(tipoCambio) || 3.70);
        }
        return costo;
    }, [cotizacion.moneda, tipoCambio]);

    // Funcion para obtener costo por hora de un personal segun moneda actual
    const getCostoHoraPersonal = useCallback((personalId) => {
        const pers = personalList.find(p => p.id === parseInt(personalId));
        if (!pers) return 0;
        return convertirCosto(pers.costo_hora);
    }, [personalList, convertirCosto]);

    // Cargar datos iniciales
    useEffect(() => {
        const cargarDatos = async () => {
            setLoading(true);
            try {
                // Cargar todos los catalogos via cache (TTL 5 min + invalidacion manual).
                // Si los datos ya estan en cache, retorna instantaneo sin hit a la API.
                const [tiposRes, tiposGroupedRes, personalRes, costosFijosRes, costosFijosGroupedRes, plantillasRes, clientesRes, usuariosRes, categoriasRes] = await Promise.all([
                    getCachedTiposServicio(),
                    getCachedTiposServicioGrouped(),
                    getCachedPersonal(),
                    getCachedCostosFijos(),
                    getCachedCostosFijosGrouped(),
                    getCachedPlantillas(),
                    getCachedClientes(),
                    getCachedUsuarios(),
                    getCachedCategorias()
                ]);

                // filter(Boolean) para descartar cualquier null/undefined que venga
                // en el array (defensa contra respuestas API con items rotos).
                setTiposServicio((tiposRes.data || []).filter(Boolean));
                setTiposServicioGrouped(tiposGroupedRes.data || []);
                setCategoriasList((categoriasRes.data || []).filter(Boolean));
                setPersonalList((personalRes.data || []).filter(Boolean));
                setCostosFijosList((costosFijosRes.data || []).filter(Boolean));
                setCostosFijosGrouped(costosFijosGroupedRes.data || []);
                // Filtrar solo plantillas activas
                setPlantillasList((plantillasRes.data || []).filter(p => p && Boolean(p.activo)));
                setClientesList((clientesRes.data || []).filter(Boolean));
                // Filtrar solo usuarios activos
                setUsuariosList((usuariosRes.data || []).filter(u => u && u.activo));

                // Cargar periodos activos (informativo, opcional por servicio).
                try {
                    const perRes = await periodosServicioAPI.getAll(true);
                    setPeriodosActivos(perRes.data || []);
                } catch (_) {
                    setPeriodosActivos([]);
                }

                // Cargar planes de pago activos (opcional, interno).
                try {
                    const plnRes = await planesPagoAPI.getAll(true);
                    setPlanesPagoActivos((plnRes?.data || []).filter(Boolean));
                } catch (_) {
                    setPlanesPagoActivos([]);
                }

                // Si es edicion, cargar cotizacion
                if (cotizacionId) {
                    const cotRes = await cotizacionesAPI.getById(cotizacionId);
                    const data = cotRes.data;

                    // Guard: solo se edita en estado 'borrador'. Cualquier otro estado
                    // (enviada/aprobada/rechazada) redirige a la vista detalle.
                    if (data.estado && data.estado !== 'borrador') {
                        navigate(`/cotizaciones/${cotizacionId}/detalle`, { replace: true });
                        return;
                    }

                    setCotizacion({
                        cliente_id: data.cliente_id || null,
                        cliente_nombre: data.cliente_nombre || '',
                        cliente_email: data.cliente_email || '',
                        cliente_telefono: data.cliente_telefono || '',
                        cliente_empresa: data.cliente_empresa || '',
                        tiempo_desarrollo: data.tiempo_desarrollo || '',
                        fecha: data.fecha?.split('T')[0] || getCurrentDate(),
                        validez: data.validez || 15,
                        moneda: data.moneda || 'PEN',
                        // MySQL devuelve BOOLEAN como 0/1; interpretamos: undefined/null → true (retrocompatible)
                        incluye_igv: data.incluye_igv === 0 || data.incluye_igv === false ? false : true,
                        comentarios: data.comentarios || '',
                        asignados_ids: (data.asignados || []).map(a => a.usuario_id)
                    });

                    // Precargar plan de pago seleccionado (opcional)
                    if (data.plan_pago_id) {
                        setPlanPagoId(data.plan_pago_id);
                    }

                    // Precargar el switch de archivo adjunto si la cotizacion ya tiene uno.
                    if (data.archivo_path) {
                        setAdjuntarArchivo(true);
                        setArchivoActual({
                            archivo_path: data.archivo_path,
                            archivo_nombre: data.archivo_nombre
                        });
                    }

                    // Si tiene cliente_id, establecer el cliente seleccionado
                    if (data.cliente_id) {
                        const clienteEncontrado = (clientesRes.data || []).find(c => c.id === data.cliente_id);
                        if (clienteEncontrado) {
                            setClienteSeleccionado({
                                value: clienteEncontrado.id,
                                label: clienteEncontrado.empresa
                                    ? `${clienteEncontrado.nombre} - ${clienteEncontrado.empresa}`
                                    : clienteEncontrado.nombre,
                                data: clienteEncontrado
                            });
                        }
                    }

                    setNumeroCotizacion(data.numero);
                    setVersionesCount(Number(data.versiones_count) || 0);
                    // Persistir TC guardado de la cotizacion (si existe).
                    // Si es null/undefined, sigue usando el global de configuracion.
                    if (data.tipo_cambio != null && data.tipo_cambio !== '') {
                        setTcCotizacionGuardado(data.tipo_cambio);
                    }
                    setUltimaActualizacion({
                        por: data.actualizado_por_nombre || null,
                        at: data.updated_at || null
                    });

                    // Mapear servicios con sus asignaciones, costos fijos y recalcular costo/total
                    const serviciosMapeados = (data.servicios || []).map(s => {
                        const asignacionesMapeadas = (s.asignaciones || []).map(a => ({
                            ...a,
                            _id: safeId(),
                            horas_adicionales: (parseFloat(a.horas) || 0) * 0.12,
                            subtotal: ((parseFloat(a.horas) || 0) + ((parseFloat(a.horas) || 0) * 0.12)) * (parseFloat(a.costo_hora) || 0)
                        }));

                        // Mapear costos fijos del servicio
                        const costosFijosMapeados = (s.costosFijos || []).map(cf => {
                            const costo = parseFloat(cf.costo) || 0;
                            const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;
                            return {
                                ...cf,
                                _id: safeId(),
                                costo: costo,
                                rentabilidad: rent,
                                subtotal: totalConRentabilidad(costo, rent)
                            };
                        });

                        // Calcular costo como suma de asignaciones
                        const costoServicio = asignacionesMapeadas.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);

                        // Total de personal: suma por fila respetando override individual
                        const totalAsignaciones = asignacionesMapeadas.reduce((acc, a) => {
                            const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                            return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
                        }, 0);

                        // Total de costos fijos: subtotal o override por fila
                        const subtotalCostosFijos = costosFijosMapeados.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

                        return {
                            ...s,
                            _id: safeId(),
                            titulo: s.titulo || s.tipo_servicio_nombre || '', // fallback para servicios legacy
                            es_opcional: !!s.es_opcional,
                            asignaciones: asignacionesMapeadas,
                            costosFijos: costosFijosMapeados,
                            costo: costoServicio,
                            total: totalAsignaciones + subtotalCostosFijos
                        };
                    });
                    setServicios(serviciosMapeados);

                    setExtras(data.extras?.map(e => ({ ...e, _id: safeId() })) || []);
                } else {
                    // Cargar prefijos activos y elegir el default como inicial.
                    let prefijoIdInicial = null;
                    try {
                        // Solo prefijos tipo='normal' — este form no maneja los manuales.
                        const prefRes = await prefijosAPI.getActivos('normal');
                        const lista = prefRes?.data || [];
                        setPrefijosActivos(lista);
                        const def = lista.find(p => p.es_default) || lista[0];
                        if (def) {
                            prefijoIdInicial = def.id;
                            setPrefijoSeleccionado(def.id);
                        }
                    } catch (e) {
                        // Sin tabla o error → fallback silencioso al legacy.
                        setPrefijosActivos([]);
                    }
                    // Obtener siguiente numero de cotizacion (con el prefijo elegido si aplica).
                    const numRes = await configuracionAPI.getNextCotizacionNumber(prefijoIdInicial);
                    setNumeroCotizacion(numRes.data.numero);
                    if (numRes.data.prefijo_id && !prefijoIdInicial) {
                        setPrefijoSeleccionado(numRes.data.prefijo_id);
                    }

                    // Asignar usuario logueado por defecto
                    if (user?.id) {
                        setCotizacion(c => ({ ...c, asignados_ids: [user.id] }));
                    }

                    // Si viene aiData (de generacion con IA), aplicarlo
                    if (aiData) {
                        // Cliente
                        if (aiData.cliente) {
                            setCotizacion(c => ({
                                ...c,
                                cliente_id: aiData.cliente.id || null,
                                cliente_nombre: aiData.cliente.nombre || '',
                                cliente_email: aiData.cliente.email || '',
                                cliente_empresa: aiData.cliente.empresa || ''
                            }));
                            if (aiData.cliente.id) {
                                const cliente = (clientesRes.data || []).find(c => c.id === aiData.cliente.id);
                                if (cliente) {
                                    setClienteSeleccionado({
                                        value: cliente.id,
                                        label: cliente.empresa || cliente.nombre
                                    });
                                }
                            }
                        }

                        // Servicios
                        const personalListRaw = personalRes.data || [];
                        const tiposListRaw = tiposRes.data || [];
                        const costosFijosListRaw = costosFijosRes.data || [];

                        const serviciosFromAI = (aiData.servicios || []).map(s => {
                            const tipoServicio = tiposListRaw.find(t => t.id === s.tipo_servicio_id);
                            // IA: solo da el esqueleto (personal, horas, costo fijo, montos).
                            // La descripcion se rellena manualmente en el form de cotizacion
                            // despues — no es algo que la IA deba inferir.
                            const asignaciones = (s.asignaciones || []).map(a => {
                                const personal = personalListRaw.find(p => p.id === a.personal_id);
                                const horas = parseFloat(a.horas) || 0;
                                const costoHora = personal ? parseFloat(personal.costo_hora) : 0;
                                return {
                                    _id: safeId(),
                                    personal_id: a.personal_id,
                                    personal_nombre: personal?.nombre || '',
                                    horas,
                                    costo_hora: costoHora,
                                    rentabilidad,
                                    horas_adicionales: horas * 0.12,
                                    subtotal: (horas + horas * 0.12) * costoHora
                                };
                            });
                            const costosFijos = (s.costos_fijos || []).map(cf => {
                                const cfData = costosFijosListRaw.find(c => c.id === cf.costo_fijo_id);
                                const costo = cfData ? parseFloat(cfData.costo) : 0;
                                return {
                                    _id: safeId(),
                                    costo_fijo_id: cf.costo_fijo_id,
                                    costo_fijo_nombre: cfData?.nombre || '',
                                    costo,
                                    cantidad: 1,
                                    rentabilidad: rentCostoFijo,
                                    subtotal: totalConRentabilidad(costo, rentCostoFijo)
                                };
                            });
                            const costoServicio = asignaciones.reduce((acc, a) => acc + a.subtotal, 0);
                            const subtotalCostosFijos = costosFijos.reduce((acc, c) => acc + c.subtotal, 0);
                            const nombreTipo = tipoServicio?.nombre || s.tipo_servicio_nombre || '';
                            return {
                                _id: safeId(),
                                tipo_servicio_id: s.tipo_servicio_id,
                                tipo_servicio_nombre: nombreTipo,
                                // Usa el titulo que sugiera la IA si viene; si no, cae al nombre del tipo.
                                titulo: (s.titulo && String(s.titulo).trim()) || nombreTipo,
                                detalle: s.detalle_html || '',
                                asignaciones,
                                costosFijos,
                                costo: costoServicio,
                                total: totalConRentabilidad(costoServicio, rentabilidad) + subtotalCostosFijos
                            };
                        });

                        if (serviciosFromAI.length > 0) {
                            setServicios(serviciosFromAI);
                        } else {
                            agregarServicioInicial();
                        }

                        // Extras
                        if (aiData.extras?.length > 0) {
                            setExtras(aiData.extras.map(e => ({
                                _id: safeId(),
                                detalle: e.detalle || '',
                                costo: parseFloat(e.costo) || 0
                            })));
                        }

                        toast.success('Cotización pre-llenada con IA. Revisa y ajusta antes de guardar.');
                    } else {
                        // Agregar un servicio vacio por defecto
                        agregarServicioInicial();
                    }
                }
            } catch (err) {
                const status = err?.status;
                const msg = err?.message || '';
                // 403/404 son flujos esperados, no bugs — logueamos como warning
                // con string plano para no disparar el dev overlay de React.
                if (status === 403 || msg.includes('No tienes acceso')) {
                    console.warn('[CotizacionForm] Acceso denegado:', msg);
                    setAccesoDenegado(msg || 'No tienes permiso para ver esta cotización.');
                } else if (status === 404 || msg.toLowerCase().includes('no encontrada') || msg.toLowerCase().includes('not found')) {
                    console.warn('[CotizacionForm] Cotizacion no encontrada:', msg);
                    navigate('/404', { replace: true });
                } else {
                    console.error('Error al cargar datos:', err?.message || err);
                    toast.error(friendlyErrorMessage(
                        err,
                        'No pudimos cargar el formulario. Recarga la página o intenta de nuevo.'
                    ));
                    // Reportar al backend — sin permiso `ver_logs_sistema` no se
                    // ven en el panel, pero quedan en la BD para debug.
                    logsAPI.reportFrontend({
                        level: 'error',
                        category: aiData ? 'cotizacion-form:ia' : 'cotizacion-form',
                        mensaje: err?.message || 'Error cargando CotizacionForm',
                        stack: err?.stack || null,
                        contexto: {
                            cotizacionId: cotizacionId || null,
                            status: err?.status || null,
                            hasAiData: !!aiData
                        },
                        url: typeof window !== 'undefined' ? window.location.href : null
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacionId, user]);

    // Funcion para agregar servicio vacio (usada en useEffect y boton)
    const agregarServicioInicial = () => {
        setServicios([{
            _id: safeId(),
            tipo_servicio_id: null,
            tipo_servicio_nombre: '',
            titulo: '',
            es_opcional: false,
            detalle: '',
            asignaciones: [],
            costosFijos: [],
            costo: 0,
            total: 0
        }]);
    };

    // Agregar servicio vacio
    // ============ REORDENAR SERVICIOS ============

    const iniciarArrastre = (e, servicioId) => {
        if (servicioArmado !== servicioId) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(servicioId)); // Firefox no arranca el drag sin datos
        setServicioArrastrado(servicioId);
    };

    const terminarArrastre = () => {
        setServicioArmado(null);
        setServicioArrastrado(null);
        setZonaDestino(null);
    };

    // La seccion donde se suelta define es_opcional; el orden del state es el que se ve
    const soltarServicio = (seccion, index) => {
        const movido = servicios.find(s => s._id === servicioArrastrado);
        if (!movido) return terminarArrastre();

        const esOpcional = seccion === 'opcional';
        const normales = servicios.filter(s => !s.es_opcional && s._id !== movido._id);
        const opcionales = servicios.filter(s => !!s.es_opcional && s._id !== movido._id);

        // El indice viene del listado con el item todavia presente: al bajarlo
        // dentro de su misma seccion hay que descontar el hueco que deja
        const mismaSeccion = !!movido.es_opcional === esOpcional;
        const posOriginal = servicios
            .filter(s => !!s.es_opcional === esOpcional)
            .findIndex(s => s._id === movido._id);
        const destinoIdx = mismaSeccion && posOriginal !== -1 && index > posOriginal ? index - 1 : index;

        const destino = esOpcional ? opcionales : normales;
        destino.splice(Math.min(destinoIdx, destino.length), 0, { ...movido, es_opcional: esOpcional });

        setServicios([...normales, ...opcionales]);
        terminarArrastre();
    };

    // El bloque entero es zona de drop: mitad de arriba inserta antes, mitad de abajo despues
    const posicionDestino = (e, index) => {
        const { top, height } = e.currentTarget.getBoundingClientRect();
        return e.clientY > top + height / 2 ? index + 1 : index;
    };

    const arrastrarSobreServicio = (e, seccion, index) => {
        if (!servicioArrastrado) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setZonaDestino({ seccion, index: posicionDestino(e, index) });
    };

    const soltarSobreServicio = (e, seccion, index) => {
        if (!servicioArrastrado) return;
        e.preventDefault();
        e.stopPropagation(); // el contenedor tambien escucha drop; sin esto se aplica dos veces
        soltarServicio(seccion, posicionDestino(e, index));
    };

    // Solo indicador visual: el hit test lo hacen los bloques
    const lineaDrop = (seccion, index) => (
        <div
            className={`servicio-drop-slot ${zonaDestino?.seccion === seccion && zonaDestino?.index === index ? 'is-active' : ''}`}
            aria-hidden="true"
        />
    );

    // Seccion sin items: necesita su propia zona porque no hay bloque que capture el drop
    const zonaVacia = (seccion, texto) => {
        const activa = zonaDestino?.seccion === seccion;
        return (
            <div
                className={`servicio-drop-zone ${activa ? 'is-active' : ''}`}
                onDragOver={(e) => {
                    if (!servicioArrastrado) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setZonaDestino({ seccion, index: 0 });
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    soltarServicio(seccion, 0);
                }}
            >
                {texto}
            </div>
        );
    };

    const agregarServicio = () => {
        setServicios(prev => [...prev, {
            _id: safeId(),
            tipo_servicio_id: null,
            tipo_servicio_nombre: '',
            titulo: '',
            es_opcional: false,
            detalle: '',
            asignaciones: [],
            costosFijos: [],
            costo: 0,
            total: 0
        }]);
    };

    // Aplicar plantilla seleccionada
    // Abrir modal de selección de contactos para el cliente actualmente seleccionado.
    // Carga la lista de contactos asociados al cliente; el usuario puede elegir uno
    // y sus datos (nombre, email, telefono) reemplazan los campos del formulario.
    const aplicarPlantilla = async (plantillaId) => {
        if (!plantillaId) return;

        try {
            const response = await plantillasAPI.getById(plantillaId);
            const plantilla = response.data;

            // Mapear servicios de la plantilla
            const serviciosMapeados = (plantilla.servicios || []).map(s => {
                // Mapear asignaciones de personal
                const asignacionesMapeadas = (s.asignaciones || []).map(a => {
                    const costoHora = getCostoHoraPersonal(a.personal_id);
                    const horas = parseFloat(a.horas) || 0;
                    const horasAdicionales = horas * 0.12;
                    const horasTotales = horas + horasAdicionales;

                    return {
                        _id: safeId(),
                        personal_id: a.personal_id,
                        personal_nombre: a.personal_nombre,
                        horas: horas,
                        horas_adicionales: horasAdicionales,
                        descripcion: a.descripcion || '',
                        costo_hora: costoHora,
                        rentabilidad: a.rentabilidad !== null ? parseFloat(a.rentabilidad) : rentabilidad,
                        subtotal: horasTotales * costoHora
                    };
                });

                // Mapear costos fijos
                const costosFijosMapeados = (s.costosFijos || []).map(cf => {
                    const costo = convertirCosto(cf.costo);
                    const rent = cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;

                    return {
                        _id: safeId(),
                        costo_fijo_id: cf.costo_fijo_id,
                        costo_fijo_nombre: cf.costo_fijo_nombre,
                        periodo_id: cf.periodo_id || null,
                        periodo_nombre: cf.periodo_nombre || null,
                        periodo_meses: cf.periodo_meses ?? null,
                        categoria_nombre: cf.categoria_nombre || '',
                        costo: costo,
                        descripcion: cf.descripcion || '',
                        rentabilidad: rent,
                        subtotal: totalConRentabilidad(costo, rent)
                    };
                });

                // Calcular totales
                const costoServicio = asignacionesMapeadas.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
                const subtotalCostosFijos = costosFijosMapeados.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

                return {
                    _id: safeId(),
                    tipo_servicio_id: s.tipo_servicio_id,
                    tipo_servicio_nombre: s.tipo_servicio_nombre,
                    titulo: s.titulo || s.tipo_servicio_nombre || '',
                    periodo_id: s.periodo_id || null,
                    detalle: s.detalle || '',
                    asignaciones: asignacionesMapeadas,
                    costosFijos: costosFijosMapeados,
                    costo: costoServicio,
                    total: totalConRentabilidad(costoServicio, rentabilidad) + subtotalCostosFijos
                };
            });

            // Mapear extras de la plantilla
            const extrasMapeados = (plantilla.extras || []).map(e => ({
                _id: safeId(),
                detalle: e.detalle || '',
                costo: cotizacion.moneda === 'USD' ? (parseFloat(e.monto) || 0) / (parseFloat(tipoCambio) || 3.70) : (parseFloat(e.monto) || 0)
            }));

            setServicios(serviciosMapeados.length > 0 ? serviciosMapeados : []);
            setExtras(extrasMapeados);

            if (serviciosMapeados.length > 0) {
                const primero = serviciosMapeados.find(s => !s.es_opcional) || serviciosMapeados[0];
                setServicioExpandido(primero._id);
            }

            toast.success(`Plantilla "${plantilla.nombre}" aplicada`);
        } catch (err) {
            console.error('Error al aplicar plantilla:', err);
            toast.error('Error al cargar la plantilla');
        }
    };

    // Actualizar datos del servicio (tipo, detalle)
    const actualizarServicio = (servicioId, campo, valor) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const updated = { ...s, [campo]: valor };

            // Si cambia el tipo de servicio
            if (campo === 'tipo_servicio_id') {
                const tipo = tiposServicio.find(t => t.id === parseInt(valor));
                updated.tipo_servicio_nombre = tipo?.nombre || '';
                updated.detalle = tipo?.descripcion || s.detalle;
                // Autocompletar titulo con el nombre del tipo si aun estaba vacio
                if (!s.titulo || !s.titulo.trim()) {
                    updated.titulo = tipo?.nombre || '';
                }
            }

            // Al cambiar el periodo, los costos fijos toman el precio configurado para ese periodo
            if (campo === 'periodo_id') {
                const per = valor ? periodosActivos.find(p => p.id === parseInt(valor, 10)) : null;

                const nuevosCostosFijos = (s.costosFijos || []).map(cf => {
                    const cfCatalogo = costosFijosList.find(c => c.id === cf.costo_fijo_id);
                    const periodoDatos = {
                        periodo_id: valor || null,
                        periodo_nombre: per?.nombre || null,
                        periodo_meses: per?.meses ?? null
                    };
                    if (!cfCatalogo) return { ...cf, ...periodoDatos };

                    const base = precioCostoFijoPorPeriodo(cfCatalogo, valor);
                    const costo = cotizacion.moneda === 'USD' ? base / (parseFloat(tipoCambio) || 3.70) : base;
                    const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;

                    return {
                        ...cf,
                        ...periodoDatos,
                        costo,
                        subtotal: totalConRentabilidad(costo, rent)
                    };
                });

                const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) =>
                    acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);
                const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                    const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                    return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
                }, 0);

                updated.costosFijos = nuevosCostosFijos;
                updated.total = totalAsignaciones + subtotalCostosFijos;
            }

            return updated;
        }));
    };

    // Agregar asignacion de personal a un servicio
    const agregarAsignacion = (servicioId) => {
        const primerPersonal = personalList[0];
        const costoHora = getCostoHoraPersonal(primerPersonal?.id);
        const horas = 10;
        const horasAdicionales = horas * 0.12;
        const horasTotales = horas + horasAdicionales;

        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevaAsignacion = {
                _id: safeId(),
                personal_id: primerPersonal?.id || null,
                personal_nombre: primerPersonal?.nombre || '',
                horas: horas,
                horas_adicionales: horasAdicionales,
                descripcion: '',
                costo_hora: costoHora,
                subtotal: horasTotales * costoHora,
                rentabilidad: rentabilidad
            };

            const nuevasAsignaciones = [...s.asignaciones, nuevaAsignacion];
            const costoAsignaciones = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const totalAsignaciones = nuevasAsignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            }, 0);
            const subtotalCostosFijos = (s.costosFijos || []).reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costo: costoAsignaciones,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Actualizar asignacion de personal
    const actualizarAsignacion = (servicioId, asignacionId, campo, valor) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevasAsignaciones = s.asignaciones.map(a => {
                if (a._id !== asignacionId) return a;

                const updated = { ...a, [campo]: valor };

                // Si cambia el personal, actualizar nombre y costo convertido
                if (campo === 'personal_id') {
                    const pers = personalList.find(p => p.id === parseInt(valor));
                    updated.personal_nombre = pers?.nombre || '';
                    updated.costo_hora = getCostoHoraPersonal(valor);
                }

                // Recalcular subtotal con 12% adicional sobre horas
                if (['horas', 'costo_hora', 'personal_id'].includes(campo)) {
                    const horas = parseFloat(updated.horas) || 0;
                    const horasAdicionales = horas * 0.12;
                    const horasTotales = horas + horasAdicionales;
                    const costoHora = parseFloat(updated.costo_hora) || 0;
                    updated.horas_adicionales = horasAdicionales;
                    updated.subtotal = horasTotales * costoHora;
                }

                return updated;
            });

            // Recalcular costo y total del servicio
            const costoAsignaciones = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const totalAsignaciones = nuevasAsignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            }, 0);
            const subtotalCostosFijos = (s.costosFijos || []).reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costo: costoAsignaciones,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Eliminar asignacion de personal — confirmacion con detalle del row.
    const eliminarAsignacion = async (servicioId, asignacionId) => {
        const servicio = servicios.find(s => s._id === servicioId);
        const asig = (servicio?.asignaciones || []).find(a => a._id === asignacionId);
        const personalNombre = asig?.personal_nombre?.trim();
        const horas = parseFloat(asig?.horas) || 0;
        const descripcion = asig?.descripcion?.trim();

        const ok = await confirm({
            title: '¿Eliminar asignación?',
            message: (
                <>
                    <p style={{ margin: '0 0 8px' }}>
                        Vas a eliminar la asignación de{' '}
                        <strong>{personalNombre || 'esta persona'}</strong>
                        {horas > 0 && <> ({horas} h)</>}.
                    </p>
                    {descripcion && (
                        <p style={{ margin: 0, color: 'rgb(var(--text-muted))', fontSize: '13px' }}>
                            Tarea: {descripcion}
                        </p>
                    )}
                </>
            ),
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            danger: true,
        });
        if (!ok) return;

        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevasAsignaciones = s.asignaciones.filter(a => a._id !== asignacionId);
            const costoAsignaciones = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const totalAsignaciones = nuevasAsignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            }, 0);

            // Incluir costos fijos en el total
            const subtotalCostosFijos = (s.costosFijos || []).reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costo: costoAsignaciones,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // ============ COSTOS FIJOS DEL SERVICIO ============

    // Precio del catalogo segun el periodo. Sin precio activo para ese periodo, el precio unico
    const precioCostoFijoPorPeriodo = (costoFijoCatalogo, periodoId) => {
        const base = parseFloat(costoFijoCatalogo?.costo) || 0;
        if (!periodoId) return base;

        const match = (costoFijoCatalogo?.precios_periodo || []).find(
            p => p.activo && p.periodo_id === parseInt(periodoId, 10)
        );
        return match ? (parseFloat(match.precio) || 0) : base;
    };

    // Agregar costo fijo a un servicio (sin parámetro crea fila vacía con primer costo fijo)
    const agregarCostoFijoServicio = (servicioId, costoFijoId = null) => {
        // Si no se pasa ID, usar el primer costo fijo disponible
        const costoFijo = costoFijoId
            ? costosFijosList.find(cf => cf.id === parseInt(costoFijoId))
            : costosFijosList[0];

        if (!costoFijo) {
            toast.error('No hay costos fijos disponibles');
            return;
        }

        // Obtener rentabilidad del costo fijo desde la BD (o usar default)
        const rentabilidadCF = costoFijo.rentabilidad !== undefined && costoFijo.rentabilidad !== null ? parseFloat(costoFijo.rentabilidad) : rentCostoFijo;

        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            // Preferimos el periodo del servicio; si el costo fijo no lo tiene, su primero configurado
            const periodosCF = (costoFijo.precios_periodo || []).filter(p => p.activo);
            const periodoElegido = periodosCF.find(p => p.periodo_id === parseInt(s.periodo_id, 10))
                || periodosCF[0]
                || null;

            const costoBase = periodoElegido
                ? (parseFloat(periodoElegido.precio) || 0)
                : (parseFloat(costoFijo.costo) || 0);
            const costoConvertido = cotizacion.moneda === 'USD' ? costoBase / (parseFloat(tipoCambio) || 3.70) : costoBase;

            const nuevoCostoFijo = {
                _id: safeId(),
                costo_fijo_id: costoFijo.id,
                costo_fijo_nombre: costoFijo.nombre,
                categoria_nombre: costoFijo.categoria_nombre || '',
                periodo_id: periodoElegido?.periodo_id || null,
                periodo_nombre: periodoElegido?.periodo_nombre || null,
                periodo_meses: periodoElegido?.periodo_meses ?? null,
                costo: costoConvertido,
                descripcion: '',
                rentabilidad: rentabilidadCF,
                subtotal: totalConRentabilidad(costoConvertido, rentabilidadCF)
            };

            const nuevosCostosFijos = [...(s.costosFijos || []), nuevoCostoFijo];
            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            // Recalcular total del servicio incluyendo costos fijos (con rentabilidad individual por asignación)
            const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            }, 0);

            return {
                ...s,
                costosFijos: nuevosCostosFijos,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Actualizar costo fijo de un servicio
    const actualizarCostoFijoServicio = (servicioId, costoFijoItemId, campo, valor) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevosCostosFijos = (s.costosFijos || []).map(cf => {
                if (cf._id !== costoFijoItemId) return cf;

                const updated = { ...cf, [campo]: valor };

                // Si cambia el costo o rentabilidad, recalcular subtotal
                if (campo === 'costo' || campo === 'rentabilidad') {
                    const costo = parseFloat(campo === 'costo' ? valor : cf.costo) || 0;
                    const rentVal = campo === 'rentabilidad' ? valor : cf.rentabilidad;
                    const rent = rentVal !== undefined && rentVal !== null ? parseFloat(rentVal) : rentCostoFijo;
                    updated.subtotal = totalConRentabilidad(costo, rent);
                }

                return updated;
            });

            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            // Recalcular total del servicio incluyendo costos fijos
            const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            }, 0);

            return {
                ...s,
                costosFijos: nuevosCostosFijos,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Eliminar costo fijo de un servicio — confirmacion con detalle.
    const eliminarCostoFijoServicio = async (servicioId, costoFijoItemId) => {
        const servicio = servicios.find(s => s._id === servicioId);
        const cf = (servicio?.costosFijos || []).find(c => c._id === costoFijoItemId);
        const cfNombre = cf?.costo_fijo_nombre?.trim();
        const cfCosto = parseFloat(cf?.costo) || 0;
        const descripcion = cf?.descripcion?.trim();

        const ok = await confirm({
            title: '¿Eliminar costo fijo?',
            message: (
                <>
                    <p style={{ margin: '0 0 8px' }}>
                        Vas a eliminar <strong>{cfNombre || 'este costo fijo'}</strong>
                        {cfCosto > 0 && <> ({moneyFmt(cfCosto, cotizacion.moneda)})</>}.
                    </p>
                    {descripcion && (
                        <p style={{ margin: 0, color: 'rgb(var(--text-muted))', fontSize: '13px' }}>
                            Nota: {descripcion}
                        </p>
                    )}
                </>
            ),
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            danger: true,
        });
        if (!ok) return;

        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevosCostosFijos = (s.costosFijos || []).filter(cf => cf._id !== costoFijoItemId);
            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            // Recalcular total del servicio incluyendo costos fijos (con rentabilidad individual por asignación)
            const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
            }, 0);

            return {
                ...s,
                costosFijos: nuevosCostosFijos,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Eliminar servicio completo — alto impacto (lleva todas las asignaciones
    // y costos fijos del servicio). Skip confirm si el servicio esta vacio.
    const eliminarServicio = async (servicioId) => {
        const servicio = servicios.find(s => s._id === servicioId);
        const numAsignaciones = (servicio?.asignaciones || []).length;
        const numCostosFijos = (servicio?.costosFijos || []).length;
        const nombreServicio = servicio?.tipo_servicio_nombre?.trim();

        // Si el servicio nunca fue tocado, no molestamos con confirm.
        if (!nombreServicio && numAsignaciones === 0 && numCostosFijos === 0) {
            setServicios(prev => prev.filter(s => s._id !== servicioId));
            return;
        }

        const ok = await confirm({
            title: '¿Eliminar servicio?',
            message: (
                <>
                    <p style={{ margin: '0 0 8px' }}>
                        Vas a eliminar <strong>{nombreServicio || 'este servicio'}</strong>.
                    </p>
                    {(numAsignaciones > 0 || numCostosFijos > 0) && (
                        <p style={{ margin: 0, color: 'rgb(var(--text-muted))', fontSize: '13px' }}>
                            Incluye {numAsignaciones} {numAsignaciones === 1 ? 'asignación de personal' : 'asignaciones de personal'}
                            {' y '}
                            {numCostosFijos} {numCostosFijos === 1 ? 'costo fijo' : 'costos fijos'}.
                        </p>
                    )}
                </>
            ),
            confirmText: 'Eliminar servicio',
            cancelText: 'Cancelar',
            danger: true,
        });
        if (!ok) return;

        setServicios(prev => prev.filter(s => s._id !== servicioId));
    };

    // Cambiar el prefijo seleccionado y refrescar el numero preview.
    // Solo aplica al crear una cotizacion nueva (no en edicion).
    const handleCambiarPrefijo = async (nuevoId) => {
        setPrefijoSeleccionado(nuevoId);
        try {
            const res = await configuracionAPI.getNextCotizacionNumber(nuevoId);
            setNumeroCotizacion(res.data.numero);
        } catch (e) {
            // Silencioso: si falla, mantenemos el numero previo.
        }
    };

    // Cambiar moneda y recalcular todos los costos
    const handleCambiarMoneda = (nuevaMoneda) => {
        if (nuevaMoneda === cotizacion.moneda) return;

        const tc = parseFloat(tipoCambio) || 3.70;
        const monedaActual = cotizacion.moneda;

        // Recalcular costos de todos los servicios
        setServicios(prev => prev.map(s => {
            // Recalcular asignaciones
            const nuevasAsignaciones = s.asignaciones.map(a => {
                // Obtener costo original en PEN del personal
                const pers = personalList.find(p => p.id === parseInt(a.personal_id));
                const costoBasePEN = parseFloat(pers?.costo_hora) || 0;

                // Convertir segun la nueva moneda
                const nuevoCosto = nuevaMoneda === 'USD' ? costoBasePEN / tc : costoBasePEN;
                const horas = parseFloat(a.horas) || 0;
                const horasAdicionales = horas * 0.12;
                const horasTotales = horas + horasAdicionales;
                const nuevoSubtotal = horasTotales * nuevoCosto;

                return {
                    ...a,
                    costo_hora: nuevoCosto,
                    horas_adicionales: horasAdicionales,
                    subtotal: nuevoSubtotal
                };
            });

            // Recalcular costos fijos
            const nuevosCostosFijos = (s.costosFijos || []).map(cf => {
                // Obtener costo original en PEN del costo fijo
                const cfOriginal = costosFijosList.find(c => c.id === cf.costo_fijo_id);
                const costoBasePEN = parseFloat(cfOriginal?.costo) || parseFloat(cf.costo) || 0;

                // Convertir segun la nueva moneda
                const nuevoCosto = nuevaMoneda === 'USD' ? costoBasePEN / tc : costoBasePEN;
                const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;

                return {
                    ...cf,
                    costo: nuevoCosto,
                    subtotal: totalConRentabilidad(nuevoCosto, rent)
                };
            });

            // Calcular costo como suma de asignaciones
            const costoServicio = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costosFijos: nuevosCostosFijos,
                costo: costoServicio,
                total: totalConRentabilidad(costoServicio, rentabilidad) + subtotalCostosFijos
            };
        }));

        // Recalcular costos de extras
        setExtras(prev => prev.map(e => {
            const costoActual = parseFloat(e.costo) || 0;
            let nuevoCosto;

            if (monedaActual === 'PEN' && nuevaMoneda === 'USD') {
                // De PEN a USD: dividir por TC
                nuevoCosto = costoActual / tc;
            } else if (monedaActual === 'USD' && nuevaMoneda === 'PEN') {
                // De USD a PEN: multiplicar por TC
                nuevoCosto = costoActual * tc;
            } else {
                nuevoCosto = costoActual;
            }

            return {
                ...e,
                costo: nuevoCosto
            };
        }));

        setCotizacion(c => ({ ...c, moneda: nuevaMoneda }));
    };

    // Agregar extra
    const agregarExtra = () => {
        setExtras(prev => [...prev, {
            _id: safeId(),
            detalle: '',
            costo: 0
        }]);
    };

    // Actualizar extra
    const actualizarExtra = (id, campo, valor) => {
        setExtras(prev => prev.map(e => {
            if (e._id !== id) return e;
            // Asegurar que costo sea numero
            const valorFinal = campo === 'costo' ? (parseFloat(valor) || 0) : valor;
            return { ...e, [campo]: valorFinal };
        }));
    };

    // Eliminar extra — confirmacion si hay datos, skip si esta vacio.
    const eliminarExtra = async (id) => {
        const extra = extras.find(e => e._id === id);
        const tieneDetalle = !!extra?.detalle && extra.detalle.replace(/<[^>]+>/g, '').trim().length > 0;
        const costo = parseFloat(extra?.costo) || 0;

        if (!tieneDetalle && costo === 0) {
            setExtras(prev => prev.filter(e => e._id !== id));
            return;
        }

        const ok = await confirm({
            title: '¿Eliminar extra?',
            message: (
                <p style={{ margin: 0 }}>
                    Vas a eliminar este extra
                    {costo > 0 && <> ({moneyFmt(costo, cotizacion.moneda)})</>}.
                </p>
            ),
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            danger: true,
        });
        if (!ok) return;

        setExtras(prev => prev.filter(e => e._id !== id));
    };

    // Calculos
    const computed = useMemo(() => {
        // Calcular costo total y subtotal de servicios en un solo paso.
        // costoTotal   = suma de costos FISICOS (subtotales sin variaciones)
        // subtotalServ = suma de totales de cada servicio CON todas las variaciones
        //                aplicadas (fila → bloque → servicio)
        // gananciaTotal = subtotalServ - costoTotal
        let costoTotal = 0;
        let subtotalServicios = 0;
        let subtotalServiciosPre = 0; // Sin ofertas — para calcular el descuento total
        // Bloque paralelo para servicios marcados como es_opcional — no suman al total.
        let subtotalOpcionales = 0;
        let subtotalOpcionalesPre = 0;

        servicios.forEach(s => {
            // Suma de filas Personal con variaciones + oferta comercial por fila
            let totalFilasPers = 0;
            let totalFilasPersPre = 0;
            (s.asignaciones || []).forEach(a => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                const subtotalAsig = parseFloat(a.subtotal) || 0;
                // El costo interno (para ganancia) igual lo contamos si es opcional o no,
                // sirve como referencia. Si molesta se puede excluir mas adelante.
                if (!s.es_opcional) costoTotal += subtotalAsig;
                const totAsigPre = rowTotalConVariaciones(subtotalAsig, rent, a.total_override, a.variaciones);
                totalFilasPersPre += totAsigPre;
                totalFilasPers += aplicarOferta(totAsigPre, a.oferta);
            });
            const totalBloquePers = aplicarVariaciones(totalFilasPers, s.variaciones_bloque_personal || []);
            const totalBloquePersPre = aplicarVariaciones(totalFilasPersPre, s.variaciones_bloque_personal || []);

            let totalFilasCf = 0;
            let totalFilasCfPre = 0;
            (s.costosFijos || []).forEach(cf => {
                const costoCF = parseFloat(cf.costo) || 0;
                if (!s.es_opcional) costoTotal += costoCF;
                const totalCfSinOferta = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                totalFilasCfPre += totalCfSinOferta;
                totalFilasCf += aplicarOferta(totalCfSinOferta, cf.oferta);
            });
            const totalBloqueCf = aplicarVariaciones(totalFilasCf, s.variaciones_bloque_costos || []);
            const totalBloqueCfPre = aplicarVariaciones(totalFilasCfPre, s.variaciones_bloque_costos || []);

            const baseServ = totalBloquePers + totalBloqueCf;
            const baseServPre = totalBloquePersPre + totalBloqueCfPre;
            const totalServPreOferta = aplicarVariaciones(baseServ, s.variaciones || []);
            const totalServSinOferta = aplicarVariaciones(baseServPre, s.variaciones || []);
            // Aplicar oferta comercial a nivel servicio entero (si aplica)
            const totalServ = aplicarOferta(totalServPreOferta, s.oferta);
            if (s.es_opcional) {
                subtotalOpcionales += totalServ;
                subtotalOpcionalesPre += totalServSinOferta;
            } else {
                subtotalServicios += totalServ;
                subtotalServiciosPre += totalServSinOferta;
            }
        });

        const gananciaTotal = subtotalServicios - costoTotal;

        const subtotalExtras = extras.reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);

        const subtotal = subtotalServicios;

        const aplicaIgv = cotizacion.incluye_igv !== false;
        const igv = aplicaIgv ? subtotal * (igvRate || 0.18) : 0;
        const igvOpcionales = aplicaIgv ? subtotalOpcionales * (igvRate || 0.18) : 0;

        const total = subtotal + igv + subtotalExtras;
        const totalOpcionales = subtotalOpcionales + igvOpcionales;

        const rate = parseFloat(tipoCambio) || 3.70;
        const otherCurrency = cotizacion.moneda === "PEN" ? "USD" : "PEN";
        const totalOther = cotizacion.moneda === "PEN" ? total / rate : total * rate;

        // Diferencia entre precios sin oferta y con oferta = ahorro visible al cliente
        const descuentoTotal = Math.max(0, subtotalServiciosPre - subtotalServicios);
        const descuentoOpcionales = Math.max(0, subtotalOpcionalesPre - subtotalOpcionales);

        return {
            costoTotal,
            subtotalServicios,
            subtotalServiciosPre,
            subtotalExtras,
            subtotalOpcionales,
            subtotalOpcionalesPre,
            subtotal,
            igv,
            igvOpcionales,
            gananciaTotal,
            total,
            totalOpcionales,
            descuentoTotal,
            descuentoOpcionales,
            otherCurrency,
            totalOther
        };
    }, [servicios, extras, igvRate, tipoCambio, cotizacion.moneda, cotizacion.incluye_igv, rentabilidad]);

    // Guardar cotizacion
    const handleGuardar = async (e) => {
        e.preventDefault();

        if (servicios.length === 0) {
            toast.error('Debe agregar al menos un servicio');
            return;
        }

        // Titulo del servicio: obligatorio. Muestra en el PDF como encabezado.
        // Si el user aun no lo llena, ofrecemos el nombre del tipo o "Servicio N".
        const idxSinTitulo = servicios.findIndex(s => !s.titulo || !String(s.titulo).trim());
        if (idxSinTitulo !== -1) {
            const nombreTipo = servicios[idxSinTitulo].tipo_servicio_nombre;
            toast.error(
                nombreTipo
                    ? `El servicio "${nombreTipo}" (#${idxSinTitulo + 1}) necesita un título`
                    : `El servicio #${idxSinTitulo + 1} necesita un título`
            );
            return;
        }

        // Si el switch de archivo esta activo, un PDF/DOC/imagen es obligatorio.
        // En edicion, si ya hay archivoActual, no hace falta subir uno nuevo.
        if (adjuntarArchivo && !archivo && !archivoActual) {
            toast.error('Adjunta un archivo o desactiva la opción "Cotización manual (adjuntar archivo)"');
            return;
        }

        setSaving(true);

        try {
            // Asociar cotización a cliente:
            //   - Si ya existe (match por nombre+empresa+email), solo enlazar (NO actualizar).
            //     Los datos del cliente y sus contactos viven en la vista de cliente.
            //   - Si no existe, crear uno nuevo con los datos basicos del form.
            const clienteData = {
                nombre: cotizacion.cliente_nombre.trim(),
                empresa: cotizacion.cliente_empresa.trim(),
                email: cotizacion.cliente_email.trim(),
                telefono: cotizacion.cliente_telefono.trim()
            };

            const clienteExistente = clientesList.find(c =>
                (c.nombre || '').toLowerCase() === clienteData.nombre.toLowerCase() &&
                (c.empresa || '').toLowerCase() === clienteData.empresa.toLowerCase() &&
                (c.email || '').toLowerCase() === clienteData.email.toLowerCase()
            );

            try {
                if (clienteExistente) {
                    // Solo asociar — no se actualizan los datos del cliente desde acá.
                    cotizacion.cliente_id = clienteExistente.id;
                } else {
                    const clienteRes = await clientesAPI.create(clienteData);
                    cotizacion.cliente_id = clienteRes.data.id;
                    invalidateClientes();
                }
            } catch (clienteErr) {
                console.error('Error al asociar cliente:', clienteErr);
            }

            const data = {
                ...cotizacion,
                tipo_cambio: tipoCambio,
                prefijo_id: !cotizacionId ? prefijoSeleccionado : undefined,
                plan_pago_id: planPagoId || null,
                servicios: servicios.map(s => {
                    // Recalcular el total del servicio con TODAS las variaciones + ofertas:
                    // filas → +vars fila → +oferta fila → suma bloque → +vars bloque → +vars servicio → +oferta servicio
                    const totalFilasPers = (s.asignaciones || []).reduce((acc, a) => {
                        const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                        const tAsig = rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
                        return acc + aplicarOferta(tAsig, a.oferta);
                    }, 0);
                    const totalBloquePers = aplicarVariaciones(totalFilasPers, s.variaciones_bloque_personal || []);
                    const totalFilasCf = (s.costosFijos || []).reduce((acc, cf) => {
                        const tCf = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                        return acc + aplicarOferta(tCf, cf.oferta);
                    }, 0);
                    const totalBloqueCf = aplicarVariaciones(totalFilasCf, s.variaciones_bloque_costos || []);
                    const baseServicio = totalBloquePers + totalBloqueCf;
                    const totalPreOferta = aplicarVariaciones(baseServicio, s.variaciones || []);
                    // El total persistido ya incluye oferta comercial (visible al cliente)
                    const totalServicioFinal = aplicarOferta(totalPreOferta, s.oferta);
                    return {
                    tipo_servicio_id: s.tipo_servicio_id,
                    tipo_servicio_nombre: s.tipo_servicio_nombre,
                    titulo: (s.titulo && String(s.titulo).trim()) || s.tipo_servicio_nombre || '',
                    periodo_id: s.periodo_id || null,
                    es_opcional: !!s.es_opcional,
                    detalle: s.detalle,
                    costo: parseFloat(s.costo) || 0,
                    total: totalServicioFinal,
                    asignaciones: s.asignaciones.map(a => ({
                        personal_id: a.personal_id,
                        personal_nombre: a.personal_nombre,
                        horas: parseFloat(a.horas) || 0,
                        descripcion: a.descripcion || '',
                        costo_hora: parseFloat(a.costo_hora) || 0,
                        rentabilidad: a.rentabilidad !== undefined && a.rentabilidad !== null ? parseFloat(a.rentabilidad) : rentabilidad,
                        subtotal: parseFloat(a.subtotal) || 0,
                        total_override: hasOverride(a.total_override) ? parseFloat(a.total_override) : null,
                        variaciones: Array.isArray(a.variaciones) ? a.variaciones : [],
                        // Oferta comercial visible al cliente (opcional)
                        oferta: a.oferta || null
                    })),
                    costosFijos: (s.costosFijos || []).map(cf => ({
                        costo_fijo_id: cf.costo_fijo_id,
                        costo_fijo_nombre: cf.costo_fijo_nombre,
                        periodo_id: cf.periodo_id || null,
                        categoria_nombre: cf.categoria_nombre || '',
                        costo: parseFloat(cf.costo) || 0,
                        descripcion: cf.descripcion || '',
                        rentabilidad: cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo,
                        subtotal: parseFloat(cf.subtotal) || 0,
                        total_override: hasOverride(cf.total_override) ? parseFloat(cf.total_override) : null,
                        variaciones: Array.isArray(cf.variaciones) ? cf.variaciones : [],
                        // Oferta comercial visible al cliente (opcional)
                        oferta: cf.oferta || null
                    })),
                    // Variaciones a nivel bloque y servicio (Fase 2)
                    variaciones_bloque_personal: Array.isArray(s.variaciones_bloque_personal) ? s.variaciones_bloque_personal : [],
                    variaciones_bloque_costos: Array.isArray(s.variaciones_bloque_costos) ? s.variaciones_bloque_costos : [],
                    variaciones: Array.isArray(s.variaciones) ? s.variaciones : [],
                    // Oferta comercial a nivel servicio entero (opcional)
                    oferta: s.oferta || null
                    };
                }),
                extras: extras.map(e => ({
                    detalle: e.detalle,
                    costo: parseFloat(e.costo) || 0
                }))
            };

            let response;
            let cotIdFinal;
            if (cotizacionId) {
                response = await cotizacionesAPI.update(cotizacionId, data);
                cotIdFinal = cotizacionId;
                toast.success('Cotización actualizada');
            } else {
                response = await cotizacionesAPI.create(data);
                cotIdFinal = response.data?.id;
                toast.success(`Cotización creada: ${response.data.numero}`);
                draft.clearDraft();
            }

            // Si el switch de archivo esta activo y hay un archivo NUEVO seleccionado,
            // subirlo despues del guardado. El backend renombra a {numero}-MANUAL.{ext}.
            if (adjuntarArchivo && archivo && cotIdFinal) {
                try {
                    const upRes = await cotizacionesAPI.subirArchivo(cotIdFinal, archivo);
                    setArchivoActual({
                        archivo_path: upRes.data.archivo_path,
                        archivo_nombre: upRes.data.archivo_nombre
                    });
                    setArchivo(null);
                } catch (uploadErr) {
                    console.error('Error subiendo archivo:', uploadErr);
                    toast.error(`Cotización guardada, pero el archivo no se subió: ${uploadErr.message}`);
                }
            }

            // Si desactivo el switch y habia archivo previo, eliminarlo del server.
            if (!adjuntarArchivo && archivoActual && cotIdFinal) {
                try {
                    await cotizacionesAPI.eliminarArchivo(cotIdFinal);
                    setArchivoActual(null);
                } catch (delErr) {
                    console.error('Error eliminando archivo:', delErr);
                }
            }

            // Al crear nueva, volver al listado.
            if (!cotizacionId && onSaved) onSaved(response.data);

        } catch (err) {
            toast.error(friendlyErrorMessage(
                err,
                'No pudimos guardar la cotización. Tus datos siguen aquí, intenta de nuevo.'
            ));
        } finally {
            setSaving(false);
        }
    };

    // Generar PDF (preview = abrir en nueva pestaña, false = descargar).
    // Si la cotizacion tiene archivo adjunto (switch ON + archivo cargado en BD),
    // usa ese archivo en vez de generar el PDF del sistema.
    const handleGenerarPDF = async (preview = false) => {
        try {
            if (adjuntarArchivo && archivoActual && cotizacionId) {
                if (preview) {
                    await cotizacionesAPI.verArchivo(cotizacionId);
                } else {
                    await cotizacionesAPI.descargarArchivo(cotizacionId);
                }
                return;
            }
            // Enriquecer servicios y sus CFs con periodo_nombre + periodo_meses.
            // El PDF los usa para el sufijo "/mensual" y el bloque "Pagos Recurrentes".
            // Para CFs sin snapshot (cotizaciones antiguas), fallback al catalogo.
            const serviciosParaPdf = servicios.map(s => {
                const perServ = s.periodo_id ? periodosActivos.find(p => p.id === s.periodo_id) : null;
                return {
                    ...s,
                    periodo_nombre: perServ?.nombre || null,
                    periodo_meses: perServ?.meses ?? null,
                    costosFijos: (s.costosFijos || []).map(cf => {
                        const cfCat = costosFijosList.find(x => x.id === cf.costo_fijo_id);
                        const perCf = cf.periodo_id
                            ? periodosActivos.find(p => p.id === cf.periodo_id)
                            : (cfCat?.periodo_id ? periodosActivos.find(p => p.id === cfCat.periodo_id) : null);
                        return {
                            ...cf,
                            periodo_nombre: cf.periodo_nombre || perCf?.nombre || cfCat?.periodo_nombre || null,
                            periodo_meses: cf.periodo_meses ?? perCf?.meses ?? cfCat?.periodo_meses ?? null
                        };
                    })
                };
            });
            // Plan de pago seleccionado (opcional) — se pasa como objeto para
            // renderizar el desglose de cuotas al final del PDF.
            const planPagoParaPdf = planPagoId
                ? planesPagoActivos.find(p => p.id === planPagoId) || null
                : null;
            await generatePdfCotizacion({
                cotizacion: {
                    ...cotizacion,
                    numero: numeroCotizacion,
                    subtotal: computed.subtotal,
                    subtotalExtras: computed.subtotalExtras,
                    igv: computed.igv,
                    total: computed.total,
                    subtotalOpcionales: computed.subtotalOpcionales,
                    igvOpcionales: computed.igvOpcionales,
                    totalOpcionales: computed.totalOpcionales,
                    plan_pago: planPagoParaPdf
                },
                servicios: serviciosParaPdf,
                extras,
                configuracion,
                tiposServicio,
                preview
            });
        } catch (err) {
            toast.error(friendlyErrorMessage(
                err,
                'No pudimos generar el PDF. Intenta de nuevo en un momento.'
            ));
        }
    };

    // Abre el mini-modal "crear servicio rapido" desde el noOptionsMessage del Select.
    // Prellena el nombre con el inputValue que el usuario tipeo.
    const abrirCrearServicio = (servicioRowId, nombreTipeado) => {
        setModalCrearServicio({
            open: true,
            servicioRowId,
            nombre: (nombreTipeado || '').trim(),
            categoria_id: null,
            guardando: false
        });
    };

    const cerrarCrearServicio = () => {
        setModalCrearServicio({
            open: false,
            servicioRowId: null,
            nombre: '',
            categoria_id: null,
            guardando: false
        });
    };

    const guardarNuevoServicio = async () => {
        const nombre = modalCrearServicio.nombre.trim();
        if (!nombre) {
            toast.error('El nombre del servicio es obligatorio');
            return;
        }
        if (!modalCrearServicio.categoria_id) {
            toast.error('La categoría es obligatoria');
            return;
        }
        setModalCrearServicio(m => ({ ...m, guardando: true }));
        try {
            const res = await tiposServicioAPI.create({
                nombre,
                categoria_id: modalCrearServicio.categoria_id,
                descripcion: ''
            });
            const nuevoId = res.data?.id;

            // Invalidar cache para que la proxima entrada al form traiga el nuevo
            // servicio (y para que si abris otro tab/cotizacion lo vea fresh).
            invalidateTiposServicio();

            // Recargar listas para que el nuevo aparezca aqui mismo ahora.
            const [tiposRes, tiposGroupedRes] = await Promise.all([
                getCachedTiposServicio(),
                getCachedTiposServicioGrouped()
            ]);
            setTiposServicio(tiposRes.data || []);
            setTiposServicioGrouped(tiposGroupedRes.data || []);

            // Autoseleccionar el nuevo servicio en la fila que lo origino.
            if (nuevoId && modalCrearServicio.servicioRowId) {
                actualizarServicio(modalCrearServicio.servicioRowId, 'tipo_servicio_id', nuevoId);
            }

            toast.success(`Servicio "${nombre}" creado y seleccionado`);
            cerrarCrearServicio();
        } catch (err) {
            toast.error(friendlyErrorMessage(err, 'No pudimos crear el servicio. Intenta de nuevo.'));
            setModalCrearServicio(m => ({ ...m, guardando: false }));
        }
    };

    const abrirHistorialEstados = async () => {
        if (!cotizacionId) return;
        setHistorialEstados({ open: true, loading: true, items: [] });
        try {
            const res = await cotizacionesAPI.getEstadoHistorial(cotizacionId);
            setHistorialEstados({ open: true, loading: false, items: res.data || [] });
        } catch (err) {
            toast.error(friendlyErrorMessage(err, 'No pudimos cargar el historial de estados.'));
            setHistorialEstados({ open: false, loading: false, items: [] });
        }
    };

    const cerrarHistorialEstados = () => {
        setHistorialEstados({ open: false, loading: false, items: [] });
    };

    const handleCopiarShareLink = async () => {
        if (!cotizacionId) return;
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopiadoLink(true);
            setTimeout(() => setCopiadoLink(false), 2000);
        } catch (err) {
            toast.error('No pudimos copiar el link al portapapeles.');
        }
    };

    if (accesoDenegado) {
        return <AccesoDenegado mensaje={accesoDenegado} />;
    }

    if (loading) {
        return <CotizacionFormSkeleton />;
    }

    return (
        <form onSubmit={handleGuardar}>
            {/* Banner de recuperacion de borrador (solo cotizacion nueva con draft en localStorage) */}
            {draft.existingDraft && (
                <div className="draft-recovery-banner" role="status" aria-live="polite">
                    <div className="draft-recovery-banner__text">
                        <strong>Borrador disponible</strong>
                        <span>
                            {(() => {
                                const mins = draft.draftAge();
                                if (mins == null) return 'Hay un borrador guardado previamente';
                                if (mins < 1) return 'Borrador guardado hace menos de un minuto';
                                if (mins === 1) return 'Borrador guardado hace 1 minuto';
                                if (mins < 60) return `Borrador guardado hace ${mins} minutos`;
                                const horas = Math.floor(mins / 60);
                                return horas === 1
                                    ? 'Borrador guardado hace 1 hora'
                                    : `Borrador guardado hace ${horas} horas`;
                            })()}
                        </span>
                    </div>
                    <div className="draft-recovery-banner__actions">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={draft.dismissExistingDraft}
                        >
                            Descartar
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => draft.applyDraft({ setCotizacion, setServicios, setExtras })}
                        >
                            Recuperar borrador
                        </Button>
                    </div>
                </div>
            )}

            {/* Selector de prefijo — solo al crear y solo si hay 2+ prefijos activos */}
            {!cotizacionId && prefijosActivos.length > 1 && (
                <Card className="prefijo-selector-card">
                    <Card.Body>
                        <div className="prefijo-selector-row">
                            <div className="prefijo-selector-info">
                                <span className="label">Numeración de esta cotización</span>
                                <p className="text-xs text-muted">
                                    Elige el prefijo. Cada uno lleva su propio contador.
                                </p>
                            </div>
                            <div className="prefijo-selector-buttons" role="group" aria-label="Prefijo de numeración">
                                {prefijosActivos.map(p => {
                                    const activo = prefijoSeleccionado === p.id;
                                    // Contenido del tooltip: nombre en negrita + descripcion + tipo.
                                    // Reemplaza el title nativo por el componente Tooltip para
                                    // consistencia visual con el resto de la app.
                                    const tooltipContent = (
                                        <div style={{ maxWidth: 220, lineHeight: 1.4 }}>
                                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{p.nombre}</div>
                                            {p.descripcion && (
                                                <div style={{ fontSize: 12, opacity: 0.85, marginBottom: p.tipo ? 4 : 0 }}>
                                                    {p.descripcion}
                                                </div>
                                            )}
                                            {p.tipo && (
                                                <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                    {p.tipo === 'manual' ? 'Histórico / Manual' : 'Normal'}
                                                </div>
                                            )}
                                        </div>
                                    );
                                    return (
                                        <Tooltip key={p.id} content={tooltipContent}>
                                            <button
                                                type="button"
                                                onClick={() => handleCambiarPrefijo(p.id)}
                                                className={`prefijo-chip ${activo ? 'is-active' : ''}`}
                                                style={activo ? { backgroundColor: p.color, borderColor: p.color, color: '#fff' } : { borderColor: p.color, color: p.color }}
                                                aria-pressed={activo}
                                                aria-label={`${p.nombre}${p.descripcion ? ' — ' + p.descripcion : ''}`}
                                            >
                                                {p.prefijo}
                                            </button>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Fila superior: USAR PLANTILLA + MONEDA lado a lado */}
            <div className="form-top-row">
                {!cotizacionId && plantillasList.length > 0 && (
                    <Card>
                        <Card.Header title="Usar plantilla" />
                        <Card.Body>
                            <Select
                                styles={selectStyles}
                                options={(() => {
                                    // Agrupar plantillas por categoría. El backend las trae ordenadas
                                    // por categoria_nombre → recorremos linealmente abriendo un grupo
                                    // nuevo cada vez que cambia la categoría.
                                    const grupos = [];
                                    plantillasList.forEach(p => {
                                        const label = p.categoria_nombre || 'Sin categoría';
                                        const ultimo = grupos[grupos.length - 1];
                                        if (!ultimo || ultimo.label !== label) {
                                            grupos.push({ label, options: [{ value: p.id, label: p.nombre }] });
                                        } else {
                                            ultimo.options.push({ value: p.id, label: p.nombre });
                                        }
                                    });
                                    return grupos;
                                })()}
                                onChange={(selected) => selected && aplicarPlantilla(selected.value)}
                                placeholder="Seleccionar plantilla..."
                                isClearable
                                isSearchable
                                noOptionsMessage={() => 'Sin plantillas'}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            <p className="text-xs text-muted mt-2">
                                Carga los servicios y extras predefinidos.
                            </p>
                        </Card.Body>
                    </Card>
                )}

                {/* Wrapper: Moneda (2/3) + IGV (1/3) en 2 boxes separados */}
                <div className="moneda-igv-row">
                    <Card className="moneda-card">
                        <Card.Header title="Moneda de la cotización" />
                        <Card.Body className="flex-1 flex flex-col">
                            <div className="moneda-buttons" role="group" aria-label="Moneda de la cotización">
                                <Button
                                    variant={cotizacion.moneda === 'PEN' ? 'primary' : 'ghost'}
                                    onClick={() => handleCambiarMoneda('PEN')}
                                    className="moneda-btn"
                                    aria-pressed={cotizacion.moneda === 'PEN'}
                                >
                                    Soles (PEN)
                                </Button>
                                <Button
                                    variant={cotizacion.moneda === 'USD' ? 'primary' : 'ghost'}
                                    onClick={() => handleCambiarMoneda('USD')}
                                    className="moneda-btn"
                                    aria-pressed={cotizacion.moneda === 'USD'}
                                >
                                    Dólares (USD)
                                </Button>
                            </div>

                            <div className="moneda-info">
                                <span className="tc-badge">Tipo de cambio: 1 USD = {tipoCambio} PEN</span>
                                {cotizacion.incluye_igv !== false && (
                                    <span className="igv-badge">IGV: {Math.round(igvRate * 100)}%</span>
                                )}
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Box independiente para el toggle IGV Sí/No. Cuando esta OFF,
                        el IGV se guarda en 0 y no aparece ni en el resumen ni en el PDF. */}
                    <Card className="igv-card">
                        <Card.Header title="¿Incluye IGV?" />
                        <Card.Body className="flex-1 flex flex-col">
                            <div className="igv-toggle-buttons" role="group" aria-label="Incluir IGV">
                                <Button
                                    variant={cotizacion.incluye_igv !== false ? 'primary' : 'ghost'}
                                    onClick={() => setCotizacion(c => ({ ...c, incluye_igv: true }))}
                                    className="igv-toggle-btn"
                                    aria-pressed={cotizacion.incluye_igv !== false}
                                >
                                    Sí ({Math.round(igvRate * 100)}%)
                                </Button>
                                <Button
                                    variant={cotizacion.incluye_igv === false ? 'primary' : 'ghost'}
                                    onClick={() => setCotizacion(c => ({ ...c, incluye_igv: false }))}
                                    className="igv-toggle-btn"
                                    aria-pressed={cotizacion.incluye_igv === false}
                                >
                                    No
                                </Button>
                            </div>
                            <p className="igv-hint">
                                {cotizacion.incluye_igv !== false
                                    ? `El total incluye IGV del ${Math.round(igvRate * 100)}%.`
                                    : 'El total NO incluye IGV. Se guardará como 0.'}
                            </p>
                        </Card.Body>
                    </Card>

                    {/* Card 3ra columna: switch "¿Es cotización manual?".
                        Cuando esta en Si, aparece mas abajo el uploader del archivo. */}
                    <Card className="manual-card">
                        <Card.Header title="¿Es cotización manual?" />
                        <Card.Body className="flex-1 flex flex-col">
                            <div className="igv-toggle-buttons" role="group" aria-label="¿Es cotización manual?">
                                <Button
                                    variant={adjuntarArchivo ? 'primary' : 'ghost'}
                                    onClick={() => setAdjuntarArchivo(true)}
                                    className="igv-toggle-btn"
                                    aria-pressed={adjuntarArchivo}
                                    type="button"
                                >
                                    Sí
                                </Button>
                                <Button
                                    variant={!adjuntarArchivo ? 'primary' : 'ghost'}
                                    onClick={() => setAdjuntarArchivo(false)}
                                    className="igv-toggle-btn"
                                    aria-pressed={!adjuntarArchivo}
                                    type="button"
                                >
                                    No
                                </Button>
                            </div>
                            <p className="igv-hint">
                                {adjuntarArchivo
                                    ? 'Adjunta el documento hecho a mano más abajo.'
                                    : 'Cotización normal — se generará el PDF automáticamente.'}
                            </p>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <Card className="mt-3.5">
                <Card.Header
                    title={cotizacionId ? `Editar Cotización ${numeroCotizacion}` : `Nueva Cotización ${numeroCotizacion}`}
                    subtitle={cotizacionId && ultimaActualizacion?.at
                        ? (ultimaActualizacion.por
                            ? `Actualizada por ${ultimaActualizacion.por} · ${formatDateTimeShort(ultimaActualizacion.at)}`
                            : `Actualizada el ${formatDateTimeShort(ultimaActualizacion.at)}`)
                        : undefined}
                    actions={cotizacionId ? (
                        <div className="flex items-center gap-2">
                            {versionesCount > 1 && (
                                <Tooltip content={`Ver ${versionesCount} versiones para esta cotización`}>
                                    <button
                                        type="button"
                                        className="versiones-badge"
                                        onClick={() => setShowHiloModal(true)}
                                        aria-label={`Ver hilo de versiones (${versionesCount})`}
                                    >
                                        <FileStack size={13} aria-hidden="true" />
                                        {versionesCount}
                                    </button>
                                </Tooltip>
                            )}
                            <Tooltip content="Ver historial de cambios de estado">
                                <Button
                                    variant="soft"
                                    size="sm"
                                    leftIcon={<History size={14} />}
                                    onClick={abrirHistorialEstados}
                                >
                                    Historial
                                </Button>
                            </Tooltip>
                            <Tooltip content={copiadoLink ? 'Link copiado al portapapeles' : 'Copiar link directo de esta cotización'}>
                                <Button
                                    variant={copiadoLink ? 'primary' : 'soft'}
                                    size="sm"
                                    leftIcon={copiadoLink ? <Check size={14} /> : <Link2 size={14} />}
                                    onClick={handleCopiarShareLink}
                                    className="transition-colors"
                                >
                                    {copiadoLink ? '¡Copiado!' : 'Copiar link'}
                                </Button>
                            </Tooltip>
                        </div>
                    ) : undefined}
                />
                <Card.Body>
                    {/* Selector de cliente y usuario asignado en 2 columnas */}
                    <div className="form-selects-row">
                    {/* Selector de cliente existente */}
                    <div className="field">
                        <span className="label">Buscar cliente existente</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <Select
                                    options={clientesList.map(c => ({
                                        value: c.id,
                                        label: c.empresa ? `${c.nombre} - ${c.empresa}` : c.nombre,
                                        data: c
                                    }))}
                                    value={clienteSeleccionado}
                                    onChange={(selected) => {
                                        setClienteSeleccionado(selected);
                                        if (selected) {
                                            setCotizacion(c => ({
                                                ...c,
                                                cliente_id: selected.data.id,
                                                cliente_nombre: selected.data.nombre || '',
                                                cliente_empresa: selected.data.empresa || '',
                                                cliente_email: selected.data.email || '',
                                                cliente_telefono: selected.data.telefono || ''
                                            }));
                                        }
                                    }}
                                    isClearable
                                    placeholder="Buscar por nombre o empresa..."
                                    noOptionsMessage={() => 'No hay clientes'}
                                    styles={selectStyles}
                                />
                            </div>
                            <ClienteAcciones
                                clienteSeleccionado={clienteSeleccionado}
                                onClientesActualizados={setClientesList}
                                onClienteCreado={(nuevo) => {
                                    setClienteSeleccionado({
                                        value: nuevo.id,
                                        label: nuevo.empresa ? `${nuevo.nombre} - ${nuevo.empresa}` : nuevo.nombre,
                                        data: nuevo
                                    });
                                    setCotizacion(c => ({
                                        ...c,
                                        cliente_id: nuevo.id,
                                        cliente_nombre: nuevo.nombre || '',
                                        cliente_empresa: nuevo.empresa || '',
                                        cliente_email: nuevo.email || '',
                                        cliente_telefono: nuevo.telefono || ''
                                    }));
                                }}
                                onContactoSeleccionado={(c) => setCotizacion(prev => ({
                                    ...prev,
                                    cliente_nombre: c.nombre || '',
                                    cliente_email: c.email || '',
                                    cliente_telefono: c.telefono || ''
                                }))}
                            />
                        </div>
                    </div>

                    {/* Selector de usuarios asignados */}
                    <div className="field">
                        <span className="label">Asignar cotización a usuarios</span>
                        <Select
                            isMulti
                            options={[
                                { label: 'Administración', options: usuariosList.filter(u => u.rol === 'superadmin' || u.rol === 'admin').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Gestores de proyecto', options: usuariosList.filter(u => u.rol === 'gestor_proyecto').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Comerciales', options: usuariosList.filter(u => u.rol === 'comercial').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Equipo operativo', options: usuariosList.filter(u => u.rol === 'personal').map(u => ({ value: u.id, label: u.nombre })) },
                            ].filter(g => g.options.length > 0)}
                            value={cotizacion.asignados_ids.map(id => {
                                const u = usuariosList.find(u => u.id === id);
                                return u ? { value: u.id, label: u.nombre } : null;
                            }).filter(Boolean)}
                            onChange={(selected) => {
                                setCotizacion(c => ({
                                    ...c,
                                    asignados_ids: (selected || []).map(s => s.value)
                                }));
                            }}
                            placeholder="Seleccionar usuarios..."
                            noOptionsMessage={() => 'No hay usuarios'}
                            styles={selectStyles}
                        />
                    </div>
                </div>

                    <div className="form-grid-3">
                        <Input
                            id="cot-cliente-nombre"
                            label="Cliente"
                            type="text"
                            value={cotizacion.cliente_nombre}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_nombre: e.target.value }))}
                            required
                            aria-required="true"
                        />

                        <Input
                            id="cot-cliente-empresa"
                            label="Empresa"
                            type="text"
                            value={cotizacion.cliente_empresa}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_empresa: e.target.value }))}
                        />

                        <Input
                            id="cot-cliente-email"
                            label="Email"
                            type="email"
                            value={cotizacion.cliente_email}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_email: e.target.value }))}
                        />

                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="cot-cliente-telefono" className="text-xs font-medium text-muted">Teléfono</label>
                            <PhoneInput
                                id="cot-cliente-telefono"
                                value={cotizacion.cliente_telefono}
                                onChange={(v) => setCotizacion(c => ({ ...c, cliente_telefono: v || '' }))}
                            />
                        </div>

                        <Input
                            id="cot-tiempo-desarrollo"
                            label="Tiempo de desarrollo"
                            type="text"
                            value={cotizacion.tiempo_desarrollo}
                            onChange={(e) => setCotizacion(c => ({ ...c, tiempo_desarrollo: e.target.value }))}
                            placeholder="Ej: 2 semanas, 1 mes"
                        />

                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="cot-fecha" className="text-xs font-medium text-muted">Fecha</label>
                            <DatePicker
                                id="cot-fecha"
                                value={cotizacion.fecha ? new Date(cotizacion.fecha + 'T00:00:00') : null}
                                onChange={(date) => setCotizacion(c => ({
                                    ...c,
                                    fecha: date ? date.toISOString().split('T')[0] : ''
                                }))}
                                format="dd/MM/yyyy"
                                oneTap
                                placement="bottomStart"
                                className="cot-form-datepicker"
                                block
                            />
                        </div>

                        <Input
                            id="cot-validez"
                            label="Validez (días)"
                            type="number"
                            min="1"
                            value={cotizacion.validez}
                            onChange={(e) => setCotizacion(c => ({ ...c, validez: parseInt(e.target.value) || 15 }))}
                        />
                    </div>
                </Card.Body>
            </Card>


            {/* Servicios */}
            <Card className="mt-3.5">
                <Card.Header
                    title="Servicios"
                    actions={
                        <Button
                            variant="primary"
                            onClick={agregarServicio}
                            leftIcon={<Plus size={16} aria-hidden="true" />}
                        >
                            Agregar servicio
                        </Button>
                    }
                />
                <Card.Body>
                    <div
                        className="servicios-list"
                        onDragOver={(e) => {
                            if (!servicioArrastrado) return;
                            // Los huecos entre bloques son del contenedor: sin esto el
                            // cursor marca "no permitido" justo donde se apunta a insertar
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={(e) => {
                            if (!servicioArrastrado || !zonaDestino) return;
                            e.preventDefault();
                            soltarServicio(zonaDestino.seccion, zonaDestino.index);
                        }}
                    >
                    {/* Si no queda ningun servicio normal, la zona de arriba sigue disponible */}
                    {servicioArrastrado && !servicios.some(s => !s.es_opcional)
                        && zonaVacia('normal', 'Suelta aquí para que sume al total')}
                    {(() => {
                        // Renderizar primero los servicios normales, luego un separador
                        // "Opcionales", luego los opcionales — sin cambiar el orden real
                        // en el state (para no romper el guardado ni el orden en BD).
                        const ordenados = [
                            ...servicios.filter(s => !s.es_opcional),
                            ...servicios.filter(s => !!s.es_opcional)
                        ];
                        const idxPrimeraOpcional = ordenados.findIndex(s => !!s.es_opcional);
                        return ordenados;
                    })().map((servicio, idx, arr) => {
                        const esOpcional = !!servicio.es_opcional;
                        // Primera opcional del bloque = insertar separador antes de este item.
                        const esPrimeraOpcional = esOpcional && arr.findIndex(s => !!s.es_opcional) === idx;

                        // Posicion dentro de su seccion, para saber donde insertar al soltar
                        const seccion = esOpcional ? 'opcional' : 'normal';
                        const totalNormales = arr.filter(s => !s.es_opcional).length;
                        const indiceEnSeccion = esOpcional ? idx - totalNormales : idx;
                        const esUltimoDeSeccion = esOpcional
                            ? idx === arr.length - 1
                            : idx === totalNormales - 1;
                        // Solo abierto si está seleccionado explícitamente
                        const isExpanded = servicioExpandido === servicio._id;

                        // Al hacer clic: si ya está abierto lo cierra, si no lo abre (cerrando otros)
                        const toggleExpand = () => setServicioExpandido(isExpanded ? null : servicio._id);

                        return (
                            <React.Fragment key={servicio._id}>
                                {esPrimeraOpcional && (
                                    <div
                                        className="costos-divider opcionales-divider"
                                        onDragOver={(e) => {
                                            if (!servicioArrastrado) return;
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                            setZonaDestino({ seccion: 'opcional', index: 0 });
                                        }}
                                        onDrop={(e) => {
                                            if (!servicioArrastrado) return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            soltarServicio('opcional', 0);
                                        }}
                                    >
                                        <h3 className="costos-divider-title">
                                            <Info size={16} aria-hidden="true" />
                                            <span>Opcionales</span>
                                        </h3>
                                        <span className="text-xs text-muted">
                                            Servicios opcionales — no se suman al total.
                                        </span>
                                    </div>
                                )}
                            {lineaDrop(seccion, indiceEnSeccion)}
                            <div
                                className={`servicio-item ${isExpanded ? 'expanded' : 'collapsed'} ${esOpcional ? 'is-opcional' : ''} ${servicioArrastrado === servicio._id ? 'is-dragging' : ''}`}
                                draggable={servicioArmado === servicio._id}
                                onDragStart={(e) => iniciarArrastre(e, servicio._id)}
                                onDragEnd={terminarArrastre}
                                onDragOver={(e) => arrastrarSobreServicio(e, seccion, indiceEnSeccion)}
                                onDrop={(e) => soltarSobreServicio(e, seccion, indiceEnSeccion)}
                            >
                                <div
                                    className="servicio-header"
                                    onClick={toggleExpand}
                                >
                                    <div className="servicio-header-left">
                                        <Tooltip content="Arrastra para reordenar">
                                            <button
                                                type="button"
                                                className="servicio-drag-handle"
                                                aria-label={`Mover servicio #${idx + 1}`}
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={() => setServicioArmado(servicio._id)}
                                                onMouseUp={() => setServicioArmado(null)}
                                            >
                                                <GripVertical size={16} aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                        <span className="servicio-number">#{idx + 1}</span>
                                        {esOpcional && (
                                            <span className="servicio-opcional-badge" title="Servicio opcional — no suma al total">Opcional</span>
                                        )}
                                        <div className="select-wrapper" onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isExpanded) {
                                                setServicioExpandido(servicio._id);
                                            }
                                        }}>
                                            <Select
                                                styles={selectStyles}
                                                options={tiposServicioGrouped}
                                                value={
                                                    tiposServicioGrouped
                                                        .flatMap(g => g.options)
                                                        .find(opt => opt.value === servicio.tipo_servicio_id) || null
                                                }
                                                onChange={(selected) => actualizarServicio(servicio._id, 'tipo_servicio_id', selected?.value || '')}
                                                placeholder="Seleccionar servicio..."
                                                isClearable
                                                isSearchable
                                                isDisabled={!isExpanded}
                                                maxMenuHeight={500}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                noOptionsMessage={({ inputValue }) => (
                                                    inputValue ? (
                                                        <button
                                                            type="button"
                                                            className="select-create-btn"
                                                            onMouseDown={(e) => {
                                                                // mouseDown antes que el blur del select cierre el menu
                                                                e.preventDefault();
                                                                abrirCrearServicio(servicio._id, inputValue);
                                                            }}
                                                        >
                                                            <Plus size={14} aria-hidden="true" />
                                                            Crear servicio "<strong>{inputValue}</strong>"
                                                        </button>
                                                    ) : 'Sin opciones'
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="servicio-header-right">
                                        {(() => {
                                            // Total del servicio derivado en tiempo real:
                                            // fila → +vars fila → suma bloque → +vars bloque → +vars servicio → +oferta
                                            const totalFilasPers = (servicio.asignaciones || []).reduce((acc, a) => {
                                                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                                                const tPre = rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
                                                return acc + aplicarOferta(tPre, a.oferta);
                                            }, 0);
                                            const totalBloquePers = aplicarVariaciones(totalFilasPers, servicio.variaciones_bloque_personal || []);
                                            const totalFilasCf = (servicio.costosFijos || []).reduce((acc, cf) => {
                                                const tPre = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                                                return acc + aplicarOferta(tPre, cf.oferta);
                                            }, 0);
                                            const totalBloqueCf = aplicarVariaciones(totalFilasCf, servicio.variaciones_bloque_costos || []);
                                            const baseServicio = totalBloquePers + totalBloqueCf;
                                            const varServicio = servicio.variaciones || [];
                                            const totalPreOferta = aplicarVariaciones(baseServicio, varServicio);
                                            // Aplicar oferta comercial (visible al cliente)
                                            const totalServicioFinal = aplicarOferta(totalPreOferta, servicio.oferta);
                                            const hayOferta = !!servicio.oferta;
                                            return (
                                                <>
                                                    <span className={`servicio-total ${priceClass}`}>
                                                        {hayOferta && (
                                                            <span className="oferta-precio-tachado" title="Precio original">
                                                                {blurMoney(totalPreOferta, cotizacion.moneda)}
                                                            </span>
                                                        )}
                                                        <span className={hayOferta ? 'oferta-precio-final' : ''}>
                                                            Total: {blurMoney(totalServicioFinal, cotizacion.moneda)}
                                                        </span>
                                                        {(() => {
                                                            const per = periodosActivos.find(p => p.id === servicio.periodo_id);
                                                            return per ? <span className="servicio-total-periodo"> / {per.nombre.toLowerCase()}</span> : null;
                                                        })()}
                                                    </span>
                                                    {verDetalles && (
                                                        <VariacionesBadge
                                                            variaciones={varServicio}
                                                            canEdit={verDetalles}
                                                            onOpen={(e) => {
                                                                e?.stopPropagation?.();
                                                                setVariacionesModal({
                                                                    open: true,
                                                                    scope: 'servicio',
                                                                    servicioLocalId: servicio._id,
                                                                    base: baseServicio,
                                                                    variaciones: varServicio,
                                                                    titulo: `Variaciones — ${servicio.tipo_servicio_nombre || 'Servicio'}`
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                    {/* Boton para aplicar/editar oferta comercial al servicio */}
                                                    <Tooltip content={hayOferta ? `Oferta: ${etiquetaOferta(servicio.oferta, cotizacion.moneda)} · ${servicio.oferta.motivo}` : 'Aplicar oferta'}>
                                                        <button
                                                            type="button"
                                                            className={`btn-icon oferta-btn ${hayOferta ? 'has-oferta' : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOfertaModal({
                                                                    open: true,
                                                                    scope: 'servicio',
                                                                    servicioLocalId: servicio._id,
                                                                    precioBase: totalPreOferta,
                                                                    oferta: servicio.oferta || null,
                                                                    titulo: `Oferta — ${servicio.tipo_servicio_nombre || 'Servicio'}`
                                                                });
                                                            }}
                                                            aria-label={hayOferta ? 'Editar oferta' : 'Aplicar oferta'}
                                                        >
                                                            <Tag size={14} aria-hidden="true" />
                                                        </button>
                                                    </Tooltip>
                                                </>
                                            );
                                        })()}
                                        <button
                                            className={`toggle-icon ${isExpanded ? 'rotated' : ''}`}
                                            type="button"
                                            aria-expanded={isExpanded}
                                            aria-controls={`servicio-content-${servicio._id}`}
                                            aria-label={isExpanded ? 'Colapsar servicio' : 'Expandir servicio'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpand();
                                            }}
                                        >
                                            <ChevronDown size={20} aria-hidden="true" />
                                        </button>
                                        <Tooltip content="Eliminar servicio">
                                            <button
                                                className="btn-icon delete"
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    eliminarServicio(servicio._id);
                                                }}
                                                aria-label="Eliminar servicio"
                                            >
                                                <X size={18} aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>

                                <div
                                    id={`servicio-content-${servicio._id}`}
                                    className={`servicio-content ${isExpanded ? 'open' : ''}`}
                                    role="region"
                                    aria-label={`Detalle del servicio ${idx + 1}`}
                                >
                                    <div className="servicio-content-inner">
                                        {/* Switch opcional: si esta ON, el servicio no suma al total y aparece en un bloque aparte. */}
                                        <div className="servicio-periodo-row">
                                            <div className="servicio-periodo-info">
                                                <strong>¿Este servicio es opcional?</strong>
                                                <span className="text-xs text-muted">
                                                    {servicio.es_opcional
                                                        ? 'No se sumará al total. Aparecerá en el bloque de opcionales.'
                                                        : 'Se incluye en el total de la cotización.'}
                                                </span>
                                            </div>
                                            <div className="servicio-periodo-controls">
                                                <button
                                                    type="button"
                                                    role="switch"
                                                    aria-checked={!!servicio.es_opcional}
                                                    className={`toggle-switch ${servicio.es_opcional ? 'is-on' : ''}`}
                                                    onClick={() => actualizarServicio(servicio._id, 'es_opcional', !servicio.es_opcional)}
                                                >
                                                    <span className="toggle-switch-track" aria-hidden="true">
                                                        <span className="toggle-switch-thumb" />
                                                        <span className="toggle-switch-opt toggle-switch-opt-on">SÍ</span>
                                                        <span className="toggle-switch-opt toggle-switch-opt-off">NO</span>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Switch iOS-style: OFF = unico. ON = elegir un periodo del catalogo. */}
                                        {periodosActivos.length > 0 && (
                                            <div className="servicio-periodo-row">
                                                <div className="servicio-periodo-info">
                                                    <strong>¿Este servicio es recurrente?</strong>
                                                    <span className="text-xs text-muted">
                                                        {servicio.periodo_id
                                                            ? 'Se mostrará el período en el total (ej. S/ 500 / mensual).'
                                                            : 'Único — sin recurrencia.'}
                                                    </span>
                                                </div>
                                                <div className="servicio-periodo-controls">
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={!!servicio.periodo_id}
                                                        className={`toggle-switch ${servicio.periodo_id ? 'is-on' : ''}`}
                                                        onClick={() => {
                                                            if (servicio.periodo_id) {
                                                                actualizarServicio(servicio._id, 'periodo_id', null);
                                                            } else {
                                                                // Default al primer periodo activo.
                                                                const pref = periodosActivos.find(p => (p.nombre || '').toLowerCase() !== 'único') || periodosActivos[0];
                                                                actualizarServicio(servicio._id, 'periodo_id', pref?.id || null);
                                                            }
                                                        }}
                                                    >
                                                        <span className="toggle-switch-track" aria-hidden="true">
                                                            <span className="toggle-switch-thumb" />
                                                            <span className="toggle-switch-opt toggle-switch-opt-on">SÍ</span>
                                                            <span className="toggle-switch-opt toggle-switch-opt-off">NO</span>
                                                        </span>
                                                    </button>
                                                    {servicio.periodo_id && (
                                                        <div className="servicio-periodo-select">
                                                            <Select
                                                                styles={selectStyles}
                                                                options={periodosActivos.map(p => ({ value: p.id, label: p.nombre }))}
                                                                value={(() => {
                                                                    const p = periodosActivos.find(x => x.id === servicio.periodo_id);
                                                                    return p ? { value: p.id, label: p.nombre } : null;
                                                                })()}
                                                                onChange={(sel) => actualizarServicio(servicio._id, 'periodo_id', sel?.value || null)}
                                                                placeholder="Seleccionar período..."
                                                                isSearchable={false}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="servicio-detalle">
                                            <div className="field">
                                                <span className="label">
                                                    Título del servicio <span className="text-danger">*</span>
                                                </span>
                                                <Input
                                                    value={servicio.titulo || ''}
                                                    onChange={(e) => actualizarServicio(servicio._id, 'titulo', e.target.value)}
                                                    placeholder="Título que engloba el detalle (ej. Desarrollo Web MVP)"
                                                    maxLength={200}
                                                />
                                            </div>
                                            <div className="field">
                                                <span className="label">Detalle del servicio</span>
                                                <TipTapEditor
                                                    value={servicio.detalle}
                                                    onChange={(html) => actualizarServicio(servicio._id, 'detalle', html)}
                                                    placeholder="Descripcion detallada del servicio..."
                                                />
                                            </div>
                                        </div>

                                        {/* Sección de Costos de Servicio */}
                                        <div className="costos-servicio-section">
                                            {/* Encabezado Personal */}
                                            <div className="costos-divider personal-divider">
                                                <h3 className="costos-divider-title">
                                                    <User size={16} aria-hidden="true" />
                                                    <span>Personal</span>
                                                </h3>
                                                <button
                                                    className="btn-add btn-add-personal"
                                                    type="button"
                                                    onClick={() => agregarAsignacion(servicio._id)}
                                                >
                                                    <Plus size={14} aria-hidden="true" />
                                                    Agregar personal
                                                </button>
                                            </div>

                                            {/* Tabla de Personal */}
                                            {(servicio.asignaciones || []).length > 0 && (
                                            <div className="asignaciones-table">
                                                <div className="asignaciones-row asignaciones-header-row">
                                                    <div className="asig-zone asig-zone-quien">
                                                        <div className="asig-col asig-personal">Personal</div>
                                                    </div>
                                                    <span className="asig-zone-divider" aria-hidden="true" />
                                                    <div className="asig-zone asig-zone-cuanto">
                                                        <div className="asig-col asig-horas">Horas</div>
                                                        {verDetalles && (
                                                            <Tooltip content="Horas adicionales">
                                                                <div className="asig-col asig-adicional">12% Adic.</div>
                                                            </Tooltip>
                                                        )}
                                                        {verDetalles && <div className="asig-col asig-costo">Costo/hr</div>}
                                                    </div>
                                                    <span className="asig-zone-divider" aria-hidden="true" />
                                                    <div className="asig-zone asig-zone-resultado">
                                                        {verDetalles && (
                                                            <Tooltip content="Margen de rentabilidad aplicado al subtotal para llegar al total facturable">
                                                                <div className="asig-col asig-rentabilidad">Rentab.</div>
                                                            </Tooltip>
                                                        )}
                                                        {verDetalles && <div className="asig-col asig-subtotal">Subtotal</div>}
                                                        {verDetalles && <div className="asig-col asig-ganancia">Ganancia</div>}
                                                        {verDetalles && <div className="asig-col asig-variacion">Var.</div>}
                                                        <div className="asig-col asig-total">Total</div>
                                                    </div>
                                                    <div className="asig-col asig-actions"></div>
                                                </div>

                                                {(servicio.asignaciones || []).map((asig) => (
                                                    <div key={asig._id} className="asignaciones-row">
                                                        <div className="asig-zone asig-zone-quien">
                                                            <div className="asig-col asig-personal">
                                                                <Select
                                                                    styles={selectStyles}
                                                                    options={personalList.map(p => ({
                                                                        value: p.id,
                                                                        label: p.nombre
                                                                    }))}
                                                                    value={
                                                                        asig.personal_id
                                                                            ? {
                                                                                value: asig.personal_id,
                                                                                label: (() => {
                                                                                    const p = personalList.find(p => p.id === asig.personal_id);
                                                                                    if (!p) return '';
                                                                                    return p.nombre;
                                                                                })()
                                                                            }
                                                                            : null
                                                                    }
                                                                    onChange={(selected) => actualizarAsignacion(servicio._id, asig._id, 'personal_id', selected?.value || '')}
                                                                    placeholder="Seleccionar..."
                                                                    isClearable
                                                                    isSearchable
                                                                    noOptionsMessage={() => 'Sin opciones'}
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                />
                                                            </div>
                                                            <input
                                                                className="input input-sm asig-desc-input"
                                                                type="text"
                                                                maxLength={255}
                                                                placeholder="Tarea / rol (uso interno)"
                                                                value={asig.descripcion || ''}
                                                                onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'descripcion', e.target.value)}
                                                            />
                                                        </div>
                                                        <span className="asig-zone-divider" aria-hidden="true" />
                                                        <div className="asig-zone asig-zone-cuanto">
                                                            <div className="asig-col asig-horas">
                                                                <input
                                                                    className="input input-sm"
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.5"
                                                                    value={asig.horas === '' || asig.horas == null ? '' : Number(asig.horas)}
                                                                    onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'horas', e.target.value)}
                                                                />
                                                            </div>
                                                            {verDetalles && (
                                                                <Tooltip content="Horas adicionales">
                                                                    <div className="asig-col asig-adicional">
                                                                        <span className="adicional-value">+{(asig.horas_adicionales || asig.horas * 0.12).toFixed(2)}</span>
                                                                    </div>
                                                                </Tooltip>
                                                            )}
                                                            {verDetalles && <div className="asig-col asig-costo">
                                                                <span className="cost-readonly">
                                                                    {moneyFmt(asig.costo_hora, cotizacion.moneda)}
                                                                </span>
                                                            </div>}
                                                        </div>
                                                        <span className="asig-zone-divider" aria-hidden="true" />
                                                        <div className="asig-zone asig-zone-resultado">
                                                            {verDetalles && <div className="asig-col asig-rentabilidad">
                                                                <div className="rentabilidad-input-wrapper">
                                                                    <input
                                                                        className="input input-sm input-rentabilidad"
                                                                        type="number"
                                                                        min="-99"
                                                                        max="99"
                                                                        step="0.5"
                                                                        value={(() => {
                                                                            const r = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                            return Number((r * 100).toFixed(2));
                                                                        })()}
                                                                        onChange={(e) => {
                                                                            const raw = parseFloat(e.target.value) || 0;
                                                                            const clamped = Math.max(-99, Math.min(99, raw));
                                                                            actualizarAsignacion(servicio._id, asig._id, 'rentabilidad', clamped / 100);
                                                                        }}
                                                                    />
                                                                    <span className="rentabilidad-percent">%</span>
                                                                </div>
                                                            </div>}
                                                            {verDetalles && <div className="asig-col asig-subtotal">
                                                                <span className="subtotal-value">
                                                                    {moneyFmt(asig.subtotal, cotizacion.moneda)}
                                                                </span>
                                                            </div>}
                                                            {verDetalles && (() => {
                                                                const rent = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                const ganancia = rowGananciaConVariaciones(asig.subtotal, rent, asig.total_override, asig.variaciones);
                                                                return (
                                                                    <div className="asig-col asig-ganancia">
                                                                        <span className={`ganancia-value ${ganancia < 0 ? 'is-negative' : ''}`}>
                                                                            {moneyFmt(ganancia, cotizacion.moneda)}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                            {verDetalles && (
                                                                <div className="asig-col asig-variacion">
                                                                    {(() => {
                                                                        const rent = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                        const baseAntesVar = rowTotal(asig.subtotal, rent, asig.total_override);
                                                                        return (
                                                                            <VariacionesBadge
                                                                                variaciones={asig.variaciones || []}
                                                                                canEdit={verDetalles}
                                                                                onOpen={() => setVariacionesModal({
                                                                                    open: true,
                                                                                    scope: 'asignacion',
                                                                                    servicioLocalId: servicio._id,
                                                                                    filaLocalId: asig._id,
                                                                                    base: baseAntesVar,
                                                                                    variaciones: asig.variaciones || [],
                                                                                    titulo: `Variaciones — ${asig.personal_nombre || 'Personal'}`
                                                                                })}
                                                                            />
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                            <div className={`asig-col asig-total ${priceClass}`}>
                                                                {(() => {
                                                                    const rent = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                    const totalEfectivo = rowTotalConVariaciones(asig.subtotal, rent, asig.total_override, asig.variaciones);
                                                                    const hayOfertaAsig = !!asig.oferta;
                                                                    const totalConOferta = aplicarOferta(totalEfectivo, asig.oferta);
                                                                    if (hayOfertaAsig) {
                                                                        return (
                                                                            <span className="total-display asig-total-con-oferta">
                                                                                <span className="oferta-precio-tachado" title="Precio original">
                                                                                    {blurMoney(totalEfectivo, cotizacion.moneda)}
                                                                                </span>
                                                                                <span className="oferta-precio-final total-value">
                                                                                    {blurMoney(totalConOferta, cotizacion.moneda)}
                                                                                </span>
                                                                            </span>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <RowVariacionesTotal
                                                                            total={totalEfectivo}
                                                                            hasLegacyOverride={hasOverride(asig.total_override)}
                                                                            moneda={cotizacion.moneda}
                                                                            blurMoney={blurMoney}
                                                                        />
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                        <div className="asig-col asig-actions">
                                                            {verDetalles && (
                                                                <Tooltip content={asig.oferta ? `Oferta: ${etiquetaOferta(asig.oferta, cotizacion.moneda)} · ${asig.oferta.motivo}` : 'Aplicar oferta al cliente'}>
                                                                    <button
                                                                        type="button"
                                                                        className={`btn-icon oferta-btn ${asig.oferta ? 'has-oferta' : ''}`}
                                                                        onClick={() => {
                                                                            const rent = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                            const totAsigPre = rowTotalConVariaciones(asig.subtotal, rent, asig.total_override, asig.variaciones);
                                                                            setOfertaModal({
                                                                                open: true,
                                                                                scope: 'asignacion',
                                                                                servicioLocalId: servicio._id,
                                                                                asigLocalId: asig._id,
                                                                                precioBase: totAsigPre,
                                                                                oferta: asig.oferta || null,
                                                                                titulo: `Oferta — ${asig.personal_nombre || 'Personal'}`
                                                                            });
                                                                        }}
                                                                        aria-label={asig.oferta ? 'Editar oferta' : 'Aplicar oferta'}
                                                                    >
                                                                        <Tag size={14} aria-hidden="true" />
                                                                    </button>
                                                                </Tooltip>
                                                            )}
                                                            <button
                                                                className="btn-icon delete"
                                                                type="button"
                                                                onClick={() => eliminarAsignacion(servicio._id, asig._id)}
                                                                aria-label="Eliminar asignación"
                                                            >
                                                                <X size={16} aria-hidden="true" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Fila de totales — respeta variaciones + ofertas de filas + variaciones del bloque */}
                                                {verDetalles && (() => {
                                                    const asigs = servicio.asignaciones || [];
                                                    const subSum = asigs.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
                                                    // Suma con variaciones + oferta de filas ya aplicadas
                                                    const totSumFilas = asigs.reduce((acc, a) => {
                                                        const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                                                        const tPre = rowTotalConVariaciones(a.subtotal, rent, a.total_override, a.variaciones);
                                                        return acc + aplicarOferta(tPre, a.oferta);
                                                    }, 0);
                                                    // Variaciones a nivel bloque se aplican sobre el total ya sumado
                                                    const varBloque = servicio.variaciones_bloque_personal || [];
                                                    const totSum = aplicarVariaciones(totSumFilas, varBloque);
                                                    const ganSum = totSum - subSum;
                                                    return (
                                                        <div className="asignaciones-row totals-row">
                                                            <div className="asig-zone asig-zone-quien">
                                                                <div className="asig-col asig-personal totals-label">Total</div>
                                                            </div>
                                                            <span className="asig-zone-divider" aria-hidden="true" />
                                                            <div className="asig-zone asig-zone-cuanto">
                                                                <div className="asig-col asig-horas" />
                                                                <div className="asig-col asig-adicional" />
                                                                <div className="asig-col asig-costo" />
                                                            </div>
                                                            <span className="asig-zone-divider" aria-hidden="true" />
                                                            <div className="asig-zone asig-zone-resultado">
                                                                <div className="asig-col asig-rentabilidad" />
                                                                <div className="asig-col asig-subtotal">
                                                                    <span className="subtotal-value">{moneyFmt(subSum, cotizacion.moneda)}</span>
                                                                </div>
                                                                <div className="asig-col asig-ganancia">
                                                                    <span className="ganancia-value">{moneyFmt(ganSum, cotizacion.moneda)}</span>
                                                                </div>
                                                                <div className="asig-col asig-variacion">
                                                                    <VariacionesBadge
                                                                        variaciones={varBloque}
                                                                        canEdit={verDetalles}
                                                                        onOpen={() => setVariacionesModal({
                                                                            open: true,
                                                                            scope: 'bloque_personal',
                                                                            servicioLocalId: servicio._id,
                                                                            base: totSumFilas,
                                                                            variaciones: varBloque,
                                                                            titulo: 'Variaciones — Total Personal'
                                                                        })}
                                                                    />
                                                                </div>
                                                                <div className="asig-col asig-total">
                                                                    <span className="total-value">{moneyFmt(totSum, cotizacion.moneda)}</span>
                                                                </div>
                                                            </div>
                                                            <div className="asig-col asig-actions" />
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            )}

                                            {/* Encabezado Costos fijos */}
                                            <div className="costos-divider costos-fijos-divider">
                                                <h3 className="costos-divider-title">
                                                    <DollarSign size={16} aria-hidden="true" />
                                                    <span>Costos fijos</span>
                                                </h3>
                                                <button
                                                    className="btn-add btn-add-costo-fijo"
                                                    type="button"
                                                    onClick={() => agregarCostoFijoServicio(servicio._id)}
                                                >
                                                    <Plus size={14} aria-hidden="true" />
                                                    Agregar costo fijo
                                                </button>
                                            </div>

                                            {(servicio.costosFijos || []).length > 0 && (
                                                <div className="costos-fijos-table">
                                                        <div className="costos-fijos-row costos-fijos-header-row">
                                                            <div className="cf-zone cf-zone-quien">
                                                                <div className="cf-col cf-select">Costo fijo</div>
                                                            </div>
                                                            <span className="cf-zone-divider" aria-hidden="true" />
                                                            <div className="cf-zone cf-zone-cuanto">
                                                                {verDetalles && <div className="cf-col cf-costo">Costo</div>}
                                                            </div>
                                                            <span className="cf-zone-divider" aria-hidden="true" />
                                                            <div className="cf-zone cf-zone-resultado">
                                                                {verDetalles && (
                                                                    <Tooltip content="Margen de rentabilidad aplicado al costo del item para llegar al total facturable">
                                                                        <div className="cf-col cf-rentabilidad">Rentab.</div>
                                                                    </Tooltip>
                                                                )}
                                                                {verDetalles && <div className="cf-col cf-subtotal">Subtotal</div>}
                                                                {verDetalles && <div className="cf-col cf-ganancia">Ganancia</div>}
                                                                {verDetalles && <div className="cf-col cf-variacion">Var.</div>}
                                                                <div className="cf-col cf-total">Total</div>
                                                            </div>
                                                            <div className="cf-col cf-actions"></div>
                                                        </div>

                                                        {(servicio.costosFijos || []).map((cf) => (
                                                            <div key={cf._id} className="costos-fijos-row">
                                                                <div className="cf-zone cf-zone-quien">
                                                                    <div className="cf-col cf-select">
                                                                        <Select
                                                                            styles={selectStyles}
                                                                            options={costosFijosGrouped}
                                                                            onMenuOpen={async () => {
                                                                                // Refresca el catalogo al abrir el select — asi el user ve
                                                                                // las ediciones recientes al CF (periodos activos, precios)
                                                                                // sin depender del TTL de 5min ni del BroadcastChannel.
                                                                                try {
                                                                                    invalidateCostosFijos();
                                                                                    const [cfRes, cfGrRes] = await Promise.all([
                                                                                        getCachedCostosFijos(),
                                                                                        getCachedCostosFijosGrouped()
                                                                                    ]);
                                                                                    setCostosFijosList((cfRes.data || []).filter(Boolean));
                                                                                    setCostosFijosGrouped(cfGrRes.data || []);
                                                                                } catch (_) { /* silent — el select sigue con lo que tenia */ }
                                                                            }}
                                                                            value={
                                                                                cf.costo_fijo_id
                                                                                    ? costosFijosGrouped
                                                                                        .flatMap(g => g.options)
                                                                                        .find(opt => opt.value === cf.costo_fijo_id) || { value: cf.costo_fijo_id, label: cf.costo_fijo_nombre }
                                                                                    : null
                                                                            }
                                                                            onChange={(selected) => {
                                                                                if (selected?.value) {
                                                                                    const cfData = costosFijosList.find(c => c.id === selected.value);
                                                                                    if (cfData) {
                                                                                        // Resolver periodo: si el CF tiene periodos con precio activo
                                                                                        // los preferimos sobre `cfData.periodo_id` (default), ya que
                                                                                        // ese default puede ser NULL en catalogos nuevos aunque
                                                                                        // haya precios por periodo configurados. Prioridad:
                                                                                        //   1. Periodo del SERVICIO (si esta en los activos del CF)
                                                                                        //   2. Periodo default de la tabla base (si tiene precio)
                                                                                        //   3. Primer periodo activo del CF
                                                                                        const periodosActivosCF = (cfData.precios_periodo || []).filter(p => p.activo);
                                                                                        let periodoElegido = null;
                                                                                        if (servicio.periodo_id) {
                                                                                            periodoElegido = periodosActivosCF.find(
                                                                                                p => p.periodo_id === parseInt(servicio.periodo_id, 10)
                                                                                            );
                                                                                        }
                                                                                        if (!periodoElegido && cfData.periodo_id) {
                                                                                            periodoElegido = periodosActivosCF.find(
                                                                                                p => p.periodo_id === parseInt(cfData.periodo_id, 10)
                                                                                            );
                                                                                        }
                                                                                        if (!periodoElegido && periodosActivosCF.length > 0) {
                                                                                            periodoElegido = periodosActivosCF[0];
                                                                                        }
                                                                                        // Precio: del periodo elegido o del costo base del catalogo.
                                                                                        const costoBase = periodoElegido
                                                                                            ? (parseFloat(periodoElegido.precio) || 0)
                                                                                            : (parseFloat(cfData.costo) || 0);
                                                                                        const costoConvertido = cotizacion.moneda === 'USD'
                                                                                            ? costoBase / (parseFloat(tipoCambio) || 3.70)
                                                                                            : costoBase;
                                                                                        const rentCF = cfData.rentabilidad !== undefined && cfData.rentabilidad !== null ? parseFloat(cfData.rentabilidad) : rentCostoFijo;
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'costo_fijo_id', selected.value);
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'costo_fijo_nombre', cfData.nombre);
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'categoria_nombre', cfData.categoria_nombre || '');
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'rentabilidad', rentCF);
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'costo', costoConvertido);
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'periodo_id', periodoElegido?.periodo_id || null);
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'periodo_nombre', periodoElegido?.periodo_nombre || null);
                                                                                        actualizarCostoFijoServicio(servicio._id, cf._id, 'periodo_meses', periodoElegido?.periodo_meses ?? null);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            placeholder="Seleccionar costo fijo..."
                                                                            isSearchable
                                                                            noOptionsMessage={() => 'Sin opciones'}
                                                                            menuPortalTarget={document.body}
                                                                            menuPosition="fixed"
                                                                        />
                                                                    </div>
                                                                    <input
                                                                        className="input input-sm asig-desc-input"
                                                                        type="text"
                                                                        maxLength={255}
                                                                        placeholder="Descripción / nota (opcional)"
                                                                        value={cf.descripcion || ''}
                                                                        onChange={(e) => actualizarCostoFijoServicio(servicio._id, cf._id, 'descripcion', e.target.value)}
                                                                    />
                                                                </div>
                                                                <span className="cf-zone-divider" aria-hidden="true" />
                                                                <div className="cf-zone cf-zone-cuanto">
                                                                    {verDetalles && <div className="cf-col cf-costo">
                                                                        <input
                                                                            className="input input-sm"
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={cf.costo}
                                                                            onChange={(e) => actualizarCostoFijoServicio(servicio._id, cf._id, 'costo', e.target.value)}
                                                                        />
                                                                    </div>}
                                                                </div>
                                                                <span className="cf-zone-divider" aria-hidden="true" />
                                                                <div className="cf-zone cf-zone-resultado">
                                                                    {verDetalles && <div className="cf-col cf-rentabilidad">
                                                                        <div className="rentabilidad-input-wrapper">
                                                                            <input
                                                                                className="input input-sm input-rentabilidad"
                                                                                type="number"
                                                                                min="-99"
                                                                                max="99"
                                                                                step="0.5"
                                                                                value={(() => {
                                                                                    const r = cf.rentabilidad !== undefined && cf.rentabilidad !== null ? cf.rentabilidad : rentCostoFijo;
                                                                                    return Number((r * 100).toFixed(2));
                                                                                })()}
                                                                                onChange={(e) => {
                                                                                    const raw = parseFloat(e.target.value) || 0;
                                                                                    const clamped = Math.max(-99, Math.min(99, raw));
                                                                                    actualizarCostoFijoServicio(servicio._id, cf._id, 'rentabilidad', clamped / 100);
                                                                                }}
                                                                            />
                                                                            <span className="rentabilidad-percent">%</span>
                                                                        </div>
                                                                    </div>}
                                                                    {verDetalles && <div className="cf-col cf-subtotal">
                                                                        <span className="subtotal-value">
                                                                            {moneyFmt(parseFloat(cf.costo) || 0, cotizacion.moneda)}
                                                                        </span>
                                                                    </div>}
                                                                    {verDetalles && (() => {
                                                                        const total = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                                                                        const ganancia = total - (parseFloat(cf.costo) || 0);
                                                                        return (
                                                                            <div className="cf-col cf-ganancia">
                                                                                <span className={`ganancia-value ${ganancia < 0 ? 'is-negative' : ''}`}>
                                                                                    {moneyFmt(ganancia, cotizacion.moneda)}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                    {verDetalles && (
                                                                        <div className="cf-col cf-variacion">
                                                                            {(() => {
                                                                                const baseAntesVar = rowTotal(cf.subtotal, 0, cf.total_override);
                                                                                return (
                                                                                    <VariacionesBadge
                                                                                        variaciones={cf.variaciones || []}
                                                                                        canEdit={verDetalles}
                                                                                        onOpen={() => setVariacionesModal({
                                                                                            open: true,
                                                                                            scope: 'costo_fijo',
                                                                                            servicioLocalId: servicio._id,
                                                                                            filaLocalId: cf._id,
                                                                                            base: baseAntesVar,
                                                                                            variaciones: cf.variaciones || [],
                                                                                            titulo: `Variaciones — ${cf.costo_fijo_nombre || 'Costo fijo'}`
                                                                                        })}
                                                                                    />
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                    <div className={`cf-col cf-total ${priceClass}`}>
                                                                        {(() => {
                                                                            const totalEfectivo = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                                                                            // Fallback: si el CF no tiene periodo_nombre en state (cotizacion antigua
                                                                            // guardada antes del feature), lo derivamos del catalogo por costo_fijo_id.
                                                                            const cfCat = costosFijosList.find(x => x.id === cf.costo_fijo_id);
                                                                            // Solo los periodos con precio activo en el catalogo de este costo fijo
                                                                            const periodosCF = (cfCat?.precios_periodo || []).filter(p => p.activo);
                                                                            const periodoIdResuelto = cf.periodo_id ?? cfCat?.periodo_id ?? null;
                                                                            const periodoResuelto = cf.periodo_nombre
                                                                                || periodosCF.find(p => p.periodo_id === periodoIdResuelto)?.periodo_nombre
                                                                                || cfCat?.periodo_nombre
                                                                                || null;

                                                                            // Chip ciclico: cambia de periodo y trae su precio, por eso no se duplica el costo fijo
                                                                            const ciclarPeriodo = (e) => {
                                                                                e?.stopPropagation();
                                                                                if (periodosCF.length < 2) return;

                                                                                const idx = periodosCF.findIndex(p => p.periodo_id === periodoIdResuelto);
                                                                                const next = periodosCF[(idx + 1) % periodosCF.length];
                                                                                const base = parseFloat(next.precio) || 0;
                                                                                const nuevoCosto = cotizacion.moneda === 'USD'
                                                                                    ? base / (parseFloat(tipoCambio) || 3.70)
                                                                                    : base;

                                                                                actualizarCostoFijoServicio(servicio._id, cf._id, 'periodo_id', next.periodo_id);
                                                                                actualizarCostoFijoServicio(servicio._id, cf._id, 'periodo_nombre', next.periodo_nombre);
                                                                                actualizarCostoFijoServicio(servicio._id, cf._id, 'periodo_meses', next.periodo_meses ?? null);
                                                                                actualizarCostoFijoServicio(servicio._id, cf._id, 'costo', nuevoCosto);
                                                                            };
                                                                            const puedeCiclar = periodosCF.length > 1;
                                                                            const siguiente = puedeCiclar && (() => {
                                                                                const idx = periodosCF.findIndex(p => p.periodo_id === periodoIdResuelto);
                                                                                return periodosCF[(idx + 1) % periodosCF.length];
                                                                            })();
                                                                            const siguienteNombre = siguiente?.periodo_nombre;

                                                                            // Oferta comercial (opcional) — aplica sobre el total efectivo
                                                                            const hayOfertaCf = !!cf.oferta;
                                                                            const totalConOferta = aplicarOferta(totalEfectivo, cf.oferta);
                                                                            return (
                                                                                <>
                                                                                    {hayOfertaCf ? (
                                                                                        <span className="total-display cf-total-con-oferta">
                                                                                            <span className="oferta-precio-tachado" title="Precio original">
                                                                                                {blurMoney(totalEfectivo, cotizacion.moneda)}
                                                                                            </span>
                                                                                            <span className="oferta-precio-final total-value">
                                                                                                {blurMoney(totalConOferta, cotizacion.moneda)}
                                                                                            </span>
                                                                                        </span>
                                                                                    ) : (
                                                                                        <RowVariacionesTotal
                                                                                            total={totalEfectivo}
                                                                                            hasLegacyOverride={hasOverride(cf.total_override)}
                                                                                            moneda={cotizacion.moneda}
                                                                                            blurMoney={blurMoney}
                                                                                        />
                                                                                    )}
                                                                                    {periodoResuelto && (
                                                                                        puedeCiclar ? (
                                                                                            <Tooltip content={`Click para cambiar a "${siguienteNombre}" y traer su precio`}>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="cf-periodo-chip"
                                                                                                    onClick={ciclarPeriodo}
                                                                                                    aria-label={`Período actual: ${periodoResuelto}. Click para cambiar a ${siguienteNombre}.`}
                                                                                                >
                                                                                                    <span>{String(periodoResuelto).toLowerCase()}</span>
                                                                                                    <RotateCcw size={10} aria-hidden="true" />
                                                                                                </button>
                                                                                            </Tooltip>
                                                                                        ) : (
                                                                                            <span className="cf-total-periodo">/ {String(periodoResuelto).toLowerCase()}</span>
                                                                                        )
                                                                                    )}
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                                <div className="cf-col cf-actions">
                                                                    {verDetalles && (
                                                                        <Tooltip content={cf.oferta ? `Oferta: ${etiquetaOferta(cf.oferta, cotizacion.moneda)} · ${cf.oferta.motivo}` : 'Aplicar oferta al cliente'}>
                                                                            <button
                                                                                type="button"
                                                                                className={`btn-icon oferta-btn ${cf.oferta ? 'has-oferta' : ''}`}
                                                                                onClick={() => {
                                                                                    const totalCfPreOferta = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                                                                                    setOfertaModal({
                                                                                        open: true,
                                                                                        scope: 'costo_fijo',
                                                                                        servicioLocalId: servicio._id,
                                                                                        cfLocalId: cf._id,
                                                                                        precioBase: totalCfPreOferta,
                                                                                        oferta: cf.oferta || null,
                                                                                        titulo: `Oferta — ${cf.costo_fijo_nombre || 'Costo fijo'}`
                                                                                    });
                                                                                }}
                                                                                aria-label={cf.oferta ? 'Editar oferta' : 'Aplicar oferta'}
                                                                            >
                                                                                <Tag size={14} aria-hidden="true" />
                                                                            </button>
                                                                        </Tooltip>
                                                                    )}
                                                                    <button
                                                                        className="btn-icon delete"
                                                                        type="button"
                                                                        onClick={() => eliminarCostoFijoServicio(servicio._id, cf._id)}
                                                                        aria-label="Eliminar costo fijo"
                                                                    >
                                                                        <X size={16} aria-hidden="true" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {/* Fila de totales — respeta variaciones de filas + variaciones del bloque */}
                                                        {verDetalles && (() => {
                                                            const cfs = servicio.costosFijos || [];
                                                            const subSum = cfs.reduce((acc, cf) => acc + (parseFloat(cf.costo) || 0), 0);
                                                            const totSumFilas = cfs.reduce((acc, cf) => acc + rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones), 0);
                                                            const varBloque = servicio.variaciones_bloque_costos || [];
                                                            const totSum = aplicarVariaciones(totSumFilas, varBloque);
                                                            const ganSum = totSum - subSum;
                                                            return (
                                                                <div className="costos-fijos-row totals-row">
                                                                    <div className="cf-zone cf-zone-quien">
                                                                        <div className="cf-col cf-select totals-label">Total</div>
                                                                    </div>
                                                                    <span className="cf-zone-divider" aria-hidden="true" />
                                                                    <div className="cf-zone cf-zone-cuanto">
                                                                        <div className="cf-col cf-costo" />
                                                                    </div>
                                                                    <span className="cf-zone-divider" aria-hidden="true" />
                                                                    <div className="cf-zone cf-zone-resultado">
                                                                        <div className="cf-col cf-rentabilidad" />
                                                                        <div className="cf-col cf-subtotal">
                                                                            <span className="subtotal-value">{moneyFmt(subSum, cotizacion.moneda)}</span>
                                                                        </div>
                                                                        <div className="cf-col cf-ganancia">
                                                                            <span className="ganancia-value">{moneyFmt(ganSum, cotizacion.moneda)}</span>
                                                                        </div>
                                                                        <div className="cf-col cf-variacion">
                                                                            <VariacionesBadge
                                                                                variaciones={varBloque}
                                                                                canEdit={verDetalles}
                                                                                onOpen={() => setVariacionesModal({
                                                                                    open: true,
                                                                                    scope: 'bloque_costos',
                                                                                    servicioLocalId: servicio._id,
                                                                                    base: totSumFilas,
                                                                                    variaciones: varBloque,
                                                                                    titulo: 'Variaciones — Total Costos Fijos'
                                                                                })}
                                                                            />
                                                                        </div>
                                                                        <div className="cf-col cf-total">
                                                                            <span className="total-value">{moneyFmt(totSum, cotizacion.moneda)}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="cf-col cf-actions" />
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {esUltimoDeSeccion && lineaDrop(seccion, indiceEnSeccion + 1)}
                            </React.Fragment>
                        );
                    })}

                    {/* Sin opcionales todavia, la seccion aparece solo mientras se arrastra */}
                    {servicioArrastrado && !servicios.some(s => s.es_opcional) && (
                        <>
                            <div className="costos-divider opcionales-divider">
                                <h3 className="costos-divider-title">
                                    <Info size={16} aria-hidden="true" />
                                    <span>Opcionales</span>
                                </h3>
                                <span className="text-xs text-muted">
                                    Servicios opcionales — no se suman al total.
                                </span>
                            </div>
                            {zonaVacia('opcional', 'Suelta aquí para marcarlo como opcional')}
                        </>
                    )}
                    </div>
                </Card.Body>
            </Card>

            {/* Extras */}
            <Card className="mt-3.5">
                <Card.Header
                    title="Extras (costo fijo)"
                    actions={
                        <Button
                            variant="primary"
                            onClick={agregarExtra}
                            leftIcon={<Plus size={16} aria-hidden="true" />}
                        >
                            Agregar extra
                        </Button>
                    }
                />
                <Card.Body>

                {extras.length > 0 && (
                    <div className="extras-list">
                        {extras.map((e) => (
                            <div key={e._id} className="extra-item">
                                <div className="extra-content">
                                    <div className="extra-detalle">
                                        <TipTapEditor
                                            value={e.detalle}
                                            onChange={(html) => actualizarExtra(e._id, 'detalle', html)}
                                            placeholder="Descripcion del extra..."
                                        />
                                    </div>
                                    <Input
                                        id={`extra-costo-${e._id}`}
                                        label={`Costo (${cotizacion.moneda})`}
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={e.costo}
                                        onChange={(ev) => actualizarExtra(e._id, 'costo', ev.target.value)}
                                        wrapperClassName="w-[150px] shrink-0"
                                    />
                                    <Tooltip content="Eliminar extra">
                                        <button
                                            className="btn-icon delete"
                                            type="button"
                                            onClick={() => eliminarExtra(e._id)}
                                            aria-label="Eliminar extra"
                                        >
                                            <X size={18} aria-hidden="true" />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {extras.length === 0 && (
                    <EmptyState
                        icon={<DollarSign size={28} aria-hidden="true" />}
                        title="Sin extras agregados"
                        description="Agrega costos puntuales que se suman al total: licencias, servicios externos, viáticos, etc."
                        action={
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={agregarExtra}
                                leftIcon={<Plus size={16} aria-hidden="true" />}
                            >
                                Agregar extra
                            </Button>
                        }
                        className="py-8"
                    />
                )}
                </Card.Body>
            </Card>

            {/* Archivo manual: aparece solo cuando el switch de arriba esta ON.
                El archivo se renombra a COT-{numero}-{cliente}-MANUAL.{ext} y reemplaza al PDF
                generado por el sistema en los botones Ver/Descargar. */}
            {adjuntarArchivo && (
            <Card className="mt-3.5">
                <Card.Header
                    title="Archivo manual"
                    subtitle="Esta cotización fue enviada como un documento hecho a mano, adjunta el documento aquí."
                />
                <Card.Body>
                    <>
                            {archivoActual && !archivo ? (
                                <div className="archivo-detalle">
                                    <div className="archivo-detalle-info">
                                        <strong>{archivoActual.archivo_nombre}</strong>
                                        <span className="text-xs text-muted">Archivo actual</span>
                                    </div>
                                    <div className="archivo-detalle-acciones">
                                        <Button
                                            variant="soft" size="sm"
                                            onClick={async () => {
                                                try { await cotizacionesAPI.verArchivo(cotizacionId); }
                                                catch (e) { toast.error(e.message); }
                                            }}
                                        >
                                            Ver
                                        </Button>
                                        <Button
                                            variant="soft" size="sm"
                                            onClick={async () => {
                                                try { await cotizacionesAPI.descargarArchivo(cotizacionId); }
                                                catch (e) { toast.error(e.message); }
                                            }}
                                        >
                                            Descargar
                                        </Button>
                                        <label className="btn-reemplazar">
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                                                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                                                style={{ display: 'none' }}
                                            />
                                            Reemplazar
                                        </label>
                                    </div>
                                </div>
                            ) : !archivo ? (
                                <label
                                    className={`archivo-uploader ${dragArchivo ? 'is-drag' : ''}`}
                                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragArchivo(true); }}
                                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragArchivo(true); }}
                                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragArchivo(false); }}
                                    onDrop={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        setDragArchivo(false);
                                        const f = e.dataTransfer.files?.[0];
                                        if (f) setArchivo(f);
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                                        onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="archivo-uploader-icon">
                                        <Plus size={32} />
                                    </div>
                                    <strong className="archivo-uploader-title">
                                        {dragArchivo ? 'Suelta el archivo aquí' : 'Arrastra un archivo o haz clic para elegir'}
                                    </strong>
                                    <div className="archivo-uploader-hint">
                                        PDF · DOC · DOCX · XLS · XLSX · PNG · JPG · WEBP
                                    </div>
                                    <div className="archivo-uploader-hint">
                                        Máximo 15MB · se renombrará como {nombreArchivoCotizacion(
                                            { ...cotizacion, numero: numeroCotizacion || 'XXX' },
                                            { manual: true }
                                        )}.ext
                                    </div>
                                </label>
                            ) : (
                                <div className="archivo-seleccionado">
                                    <strong className="archivo-nombre">{archivo.name}</strong>
                                    <span className="text-xs text-muted">{(archivo.size / 1024).toFixed(1)} KB</span>
                                    <button
                                        type="button"
                                        className="archivo-quitar"
                                        onClick={() => setArchivo(null)}
                                        aria-label="Quitar archivo"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                    </>
                </Card.Body>
            </Card>
            )}

            {/* Comentarios */}
            <Card className="mt-3.5">
                <Card.Header title="Comentarios" />
                <Card.Body>
                    <div className="field">
                        <TipTapEditor
                            value={cotizacion.comentarios}
                            onChange={(html) => setCotizacion(c => ({ ...c, comentarios: html }))}
                            placeholder="Comentarios adicionales para la cotización..."
                        />
                    </div>
                </Card.Body>
            </Card>

            {/* Anexos globales del hilo (compartidos entre todas las versiones) */}
            <AnexosSection cotizacionId={cotizacionId} />

            {/* Resumen y acciones */}
            <Card className="mt-3.5">
                <Card.Header title="Resumen" />
                <Card.Body>
                    <div className="summary-grid">
                    <div className="summary">
                        {/* Valores internos - no van al PDF */}
                        {verDetalles && (
                            <>
                                <div className="sumRow interno-row">
                                    <span className="sumLabel">Costo Total</span>
                                    <span className="sumValue">{moneyFmt(computed.costoTotal, cotizacion.moneda)}</span>
                                </div>
                                <div className="sumRow interno-row">
                                    <span className="sumLabel">Ganancia Total</span>
                                    <span className={`sumValue ${computed.gananciaTotal >= 0 ? 'ganancia-positiva' : 'ganancia-negativa'}`}>
                                        {moneyFmt(computed.gananciaTotal, cotizacion.moneda)}
                                    </span>
                                </div>
                                <div className="divider interno-divider" />
                            </>
                        )}

                        {/* Valores que van al PDF */}
                        {computed.descuentoTotal > 0 ? (
                            <>
                                {/* Subtotal SIN oferta primero, luego el descuento, luego el subtotal final */}
                                <div className={`sumRow ${priceClass}`}>
                                    <span className="sumLabel">Subtotal</span>
                                    <span className="sumValue">{blurMoney(computed.subtotalServiciosPre, cotizacion.moneda)}</span>
                                </div>
                                <div className={`sumRow ${priceClass}`}>
                                    <span className="sumLabel">Descuentos aplicados</span>
                                    <span className="sumValue">− {blurMoney(computed.descuentoTotal, cotizacion.moneda)}</span>
                                </div>
                                <div className={`sumRow ${priceClass}`}>
                                    <span className="sumLabel">Subtotal tras descuentos</span>
                                    <span className="sumValue">{blurMoney(computed.subtotal, cotizacion.moneda)}</span>
                                </div>
                            </>
                        ) : (
                            <div className={`sumRow ${priceClass}`}>
                                <span className="sumLabel">Subtotal</span>
                                <span className="sumValue">{blurMoney(computed.subtotal, cotizacion.moneda)}</span>
                            </div>
                        )}
                        {cotizacion.incluye_igv !== false && (
                            <div className={`sumRow ${priceClass}`}>
                                <span className="sumLabel">IGV ({Math.round(igvRate * 100)}%)</span>
                                <span className="sumValue">{blurMoney(computed.igv, cotizacion.moneda)}</span>
                            </div>
                        )}
                        <div className={`sumRow ${priceClass}`}>
                            <span className="sumLabel">Extras</span>
                            <span className="sumValue">{blurMoney(computed.subtotalExtras, cotizacion.moneda)}</span>
                        </div>
                        <div className="divider" />
                        <div className={`sumRow big ${priceClass}`}>
                            <span className="sumLabel">TOTAL</span>
                            <span className="sumValue">{blurMoney(computed.total, cotizacion.moneda)}</span>
                        </div>
                        <div className={`pill ${priceClass}`}>
                            Equivale a aprox. {blurMoney(computed.totalOther, computed.otherCurrency)}
                        </div>

                        {computed.subtotalOpcionales > 0 && (
                            <>
                                <div className="divider" />
                                {computed.descuentoOpcionales > 0 ? (
                                    <>
                                        <div className={`sumRow ${priceClass}`}>
                                            <span className="sumLabel">Subtotal opcionales</span>
                                            <span className="sumValue">{blurMoney(computed.subtotalOpcionalesPre, cotizacion.moneda)}</span>
                                        </div>
                                        <div className={`sumRow ${priceClass}`}>
                                            <span className="sumLabel">Descuentos aplicados</span>
                                            <span className="sumValue">− {blurMoney(computed.descuentoOpcionales, cotizacion.moneda)}</span>
                                        </div>
                                        <div className={`sumRow ${priceClass}`}>
                                            <span className="sumLabel">Subtotal tras descuentos</span>
                                            <span className="sumValue">{blurMoney(computed.subtotalOpcionales, cotizacion.moneda)}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className={`sumRow ${priceClass}`}>
                                        <span className="sumLabel">Subtotal opcionales</span>
                                        <span className="sumValue">{blurMoney(computed.subtotalOpcionales, cotizacion.moneda)}</span>
                                    </div>
                                )}
                                {cotizacion.incluye_igv !== false && (
                                    <div className={`sumRow ${priceClass}`}>
                                        <span className="sumLabel">IGV opcionales ({Math.round(igvRate * 100)}%)</span>
                                        <span className="sumValue">{blurMoney(computed.igvOpcionales, cotizacion.moneda)}</span>
                                    </div>
                                )}
                                <div className={`sumRow ${priceClass} opcional-row`}>
                                    <span className="sumLabel">Total opcionales</span>
                                    <span className="sumValue">{blurMoney(computed.totalOpcionales, cotizacion.moneda)}</span>
                                </div>
                                <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                                    Servicios opcionales — no incluidos en el total.
                                </div>
                            </>
                        )}

                        {/* Selector de plan de pago (uso interno). Cards seleccionables
                            en lugar de select nativo — cada card muestra nombre + hitos
                            y se marca visualmente al elegirla. */}
                        {Array.isArray(planesPagoActivos) && planesPagoActivos.length > 0 && (() => {
                            const planes = planesPagoActivos.filter(Boolean);
                            if (planes.length === 0) return null;
                            const planActivo = !!planPagoId;
                            const planSel = planes.find(p => p && p.id === planPagoId);
                            const totalRef = parseFloat(computed.total) || 0;
                            const toggle = () => {
                                if (planActivo) setPlanPagoId(null);
                                else setPlanPagoId(planes[0]?.id || null);
                            };
                            return (
                                <div className="plan-pago-block">
                                    <div className="divider" />
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={planActivo}
                                        className={`plan-pago-block-header ${planActivo ? 'is-active' : ''}`}
                                        onClick={toggle}
                                    >
                                        <span className="plan-pago-block-title">
                                            <CreditCard size={16} aria-hidden="true" />
                                            <span>Plan de pago</span>
                                        </span>
                                        <span className="plan-pago-block-hint text-xs text-muted">
                                            {planActivo
                                                ? (planSel ? `Hasta ${(planSel.periodos || []).length} cuota${(planSel.periodos || []).length === 1 ? '' : 's'} · ${planSel.nombre || ''}` : 'Elige un plan')
                                                : 'Distribuir el total en cuotas'}
                                        </span>
                                        <span
                                            className={`toggle-switch ${planActivo ? 'is-on' : ''}`}
                                            aria-hidden="true"
                                        >
                                            <span className="toggle-switch-track">
                                                <span className="toggle-switch-thumb" />
                                                <span className="toggle-switch-opt toggle-switch-opt-on">SÍ</span>
                                                <span className="toggle-switch-opt toggle-switch-opt-off">NO</span>
                                            </span>
                                        </span>
                                    </button>

                                    {planActivo && (
                                        <>
                                            <div className="plan-pago-cards" role="radiogroup" aria-label="Elegir plan de pago">
                                                {planes.map(p => {
                                                    if (!p) return null;
                                                    const n = (p.periodos || []).length;
                                                    const activa = p.id === planPagoId;
                                                    return (
                                                        <button
                                                            key={p.id}
                                                            type="button"
                                                            role="radio"
                                                            aria-checked={activa}
                                                            className={`plan-pago-card ${activa ? 'is-active' : ''}`}
                                                            onClick={() => setPlanPagoId(p.id)}
                                                        >
                                                            <span className="plan-pago-card-name">{p.nombre || 'Plan sin nombre'}</span>
                                                            <span className="plan-pago-card-hitos">Hasta {n} cuota{n === 1 ? '' : 's'}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {planSel && (
                                                <div className="plan-pago-desglose">
                                                    {planSel.descripcion && (
                                                        <div className="plan-pago-desglose-desc">{planSel.descripcion}</div>
                                                    )}
                                                    {calcularMontosPlanPago(totalRef, planSel.periodos || []).map(per => (
                                                        <div key={per.id} className="plan-pago-desglose-row">
                                                            <span className="plan-pago-desglose-num">#{per.numero_periodo}</span>
                                                            <span className="plan-pago-desglose-nombre">{per.nombre}</span>
                                                            <span className="plan-pago-desglose-pct">{per.porcentaje}%</span>
                                                            <span className="plan-pago-desglose-monto">{blurMoney(per.monto, cotizacion.moneda)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    <div className="actions-box" aria-busy={saving} aria-live="polite">
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={saving}
                        >
                            {saving ? 'Guardando...' : (cotizacionId ? 'Actualizar' : 'Guardar Cotización')}
                        </Button>
                        {draft.enabled && draft.lastSavedAt && (
                            <Tooltip content={draft.lastSavedAt.toLocaleString()}>
                                <span className="draft-saved-hint">
                                    Borrador guardado · {draft.lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </Tooltip>
                        )}

                        {tienePermiso('ver_pdf') && (
                            <>
                                <Button
                                    variant="ghost"
                                    onClick={() => handleGenerarPDF(true)}
                                    disabled={saving}
                                >
                                    Previsualizar PDF
                                </Button>

                                <Button
                                    variant="ghost"
                                    onClick={() => handleGenerarPDF(false)}
                                    disabled={saving}
                                >
                                    Descargar PDF
                                </Button>
                            </>
                        )}

                        {onCancel && (
                            <Button
                                variant="ghost"
                                onClick={onCancel}
                                disabled={saving}
                            >
                                Cancelar
                            </Button>
                        )}
                    </div>
                    </div>
                </Card.Body>
            </Card>


            {/* Modal para ver datos del cliente — usa primitivo <Modal> con a11y completo */}
            <Modal
                open={showClienteModal && !!clienteSeleccionado}
                onClose={() => setShowClienteModal(false)}
                title="Datos del Cliente"
                size="md"
                footer={
                    <Button
                        variant="ghost"
                        onClick={() => setShowClienteModal(false)}
                    >
                        Cerrar
                    </Button>
                }
            >
                {clienteSeleccionado && (
                    <dl className="cliente-detalle-dl">
                        <div className="cliente-detalle-row">
                            <dt className="cliente-detalle-label">Nombre</dt>
                            <dd className="cliente-detalle-value">{clienteSeleccionado.data.nombre || '-'}</dd>
                        </div>
                        <div className="cliente-detalle-row">
                            <dt className="cliente-detalle-label">Empresa</dt>
                            <dd className="cliente-detalle-value">{clienteSeleccionado.data.empresa || '-'}</dd>
                        </div>
                        <div className="cliente-detalle-row">
                            <dt className="cliente-detalle-label">Email</dt>
                            <dd className="cliente-detalle-value">{clienteSeleccionado.data.email || '-'}</dd>
                        </div>
                        <div className="cliente-detalle-row">
                            <dt className="cliente-detalle-label">Teléfono</dt>
                            <dd className="cliente-detalle-value">{clienteSeleccionado.data.telefono || '-'}</dd>
                        </div>

                        {clienteSeleccionado.data.notas && (
                            <>
                                <hr className="cliente-detalle-divider" />
                                <div className="cliente-detalle-row">
                                    <dt className="cliente-detalle-label">Notas</dt>
                                    <dd className="cliente-detalle-value">{clienteSeleccionado.data.notas}</dd>
                                </div>
                            </>
                        )}
                    </dl>
                )}
            </Modal>

            {/* Mini-modal "crear servicio rapido": se dispara desde el noOptionsMessage
                del Select de servicio cuando el usuario tipea algo que no existe. */}
            <Modal
                open={modalCrearServicio.open}
                onClose={modalCrearServicio.guardando ? undefined : cerrarCrearServicio}
                title="Crear servicio"
                size="sm"
                footer={
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={cerrarCrearServicio}
                            disabled={modalCrearServicio.guardando}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={guardarNuevoServicio}
                            disabled={modalCrearServicio.guardando || !modalCrearServicio.nombre.trim() || !modalCrearServicio.categoria_id}
                        >
                            {modalCrearServicio.guardando ? 'Creando...' : 'Crear servicio'}
                        </Button>
                    </>
                }
            >
                <div className="flex flex-col gap-3">
                    <Input
                        label="Nombre del servicio"
                        value={modalCrearServicio.nombre}
                        onChange={(e) => setModalCrearServicio(m => ({ ...m, nombre: e.target.value }))}
                        placeholder="Ej. Landing institucional"
                        autoFocus
                        disabled={modalCrearServicio.guardando}
                    />
                    <div className="field">
                        <span className="label">Categoría</span>
                        <Select
                            styles={selectStyles}
                            options={categoriasList.map(c => ({ value: c.id, label: c.nombre }))}
                            value={
                                categoriasList
                                    .map(c => ({ value: c.id, label: c.nombre }))
                                    .find(opt => opt.value === modalCrearServicio.categoria_id) || null
                            }
                            onChange={(selected) =>
                                setModalCrearServicio(m => ({ ...m, categoria_id: selected?.value || null }))
                            }
                            placeholder="Seleccionar categoría..."
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isDisabled={modalCrearServicio.guardando}
                        />
                    </div>
                </div>
            </Modal>

            {/* Modal de historial de estados — cronologia de cambios con fecha y usuario.
                Cumple ley DESIGN: Esc + click overlay para cerrar (Modal primitivo). */}
            <Modal
                open={historialEstados.open}
                onClose={cerrarHistorialEstados}
                title="Historial de estados"
                size="md"
            >
                {historialEstados.loading ? (
                    <p className="text-sm text-muted">Cargando historial…</p>
                ) : historialEstados.items.length === 0 ? (
                    <p className="text-sm text-muted">
                        Esta cotización aún no tiene cambios de estado registrados.
                        El historial empezará a llenarse desde el próximo cambio.
                    </p>
                ) : (
                    <ol className="estado-timeline">
                        {historialEstados.items.map((h, idx) => (
                            <li key={h.id} className="estado-timeline-item">
                                <span
                                    className="estado-timeline-dot"
                                    style={{ background: getEstadoColor(h.estado_nuevo) }}
                                    aria-hidden="true"
                                />
                                {idx < historialEstados.items.length - 1 && (
                                    <span className="estado-timeline-line" aria-hidden="true" />
                                )}
                                <div className="estado-timeline-body">
                                    <div className="estado-timeline-header">
                                        {h.estado_anterior && (
                                            <span className="estado-timeline-prev">
                                                {h.estado_anterior}
                                            </span>
                                        )}
                                        {h.estado_anterior && <span className="estado-timeline-arrow">→</span>}
                                        <span
                                            className="estado-timeline-new"
                                            style={{ color: getEstadoColor(h.estado_nuevo) }}
                                        >
                                            {h.estado_nuevo}
                                        </span>
                                    </div>
                                    <div className="estado-timeline-meta">
                                        {h.cambiado_por_nombre || 'Usuario eliminado'}
                                        {' · '}
                                        {formatDateTimeShort(h.cambiado_at)}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </Modal>

            {/* Modal del hilo de versiones — abierto desde el badge "vN" del header */}
            <HiloVersionesModal
                open={showHiloModal}
                cotizacionId={cotizacionId}
                onClose={() => setShowHiloModal(false)}
                onChange={() => {
                    // Recargar para actualizar versiones_count y estado de principal
                    // tras marcar principal o cambios desde el modal.
                    // Como un reload completo es seguro y simple, lo hago aqui.
                    if (cotizacionId) {
                        cotizacionesAPI.getById(cotizacionId).then(res => {
                            setVersionesCount(Number(res.data.versiones_count) || 0);
                        }).catch(() => {});
                    }
                }}
            />

            {/* Modal de solicitud de edicion: aparece cuando otro user solicita
                que cedas el lock. Bloqueante: no se cierra con Esc/overlay para que
                el user tome una decision explicita. */}
            <Modal
                open={!!solicitudVisible}
                onClose={() => {}}
                closeOnOverlay={false}
                showClose={false}
                title="Otro usuario quiere editar"
                size="sm"
                footer={
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleMantenerLock}
                        >
                            Seguir editando
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleCederLock}
                        >
                            Ceder el control
                        </Button>
                    </>
                }
            >
                <p style={{ marginBottom: 8 }}>
                    <strong>{solicitud?.usuario_nombre}</strong> quiere editar esta cotización.
                </p>
                <p className="text-sm text-muted">
                    Si cedes, tu sesión de edición se cerrará y {solicitud?.usuario_nombre} podrá tomar el control.
                    Tus cambios sin guardar se perderán.
                </p>
            </Modal>

            {/* Modal de variaciones de precio (descuentos + extras). Se abre desde
                el badge de cada fila (Personal o Costos Fijos). El state esta en el
                form, no se persiste al backend hasta que se guarda la cotizacion. */}
            <VariacionesModal
                open={!!variacionesModal.open}
                onClose={() => setVariacionesModal({ open: false })}
                base={variacionesModal.base || 0}
                variaciones={variacionesModal.variaciones || []}
                onChange={aplicarCambiosVariaciones}
                titulo={variacionesModal.titulo || 'Variaciones de precio'}
                moneda={cotizacion.moneda}
                readOnly={!verDetalles}
            />

            {/* Modal para OFERTAS comerciales (descuentos visibles al cliente).
                Aplica a un servicio entero o a un CF individual. El state se guarda
                en servicio.oferta / cf.oferta y viaja al backend en el payload. */}
            <OfertaModal
                open={!!ofertaModal.open}
                onClose={() => setOfertaModal({ open: false })}
                onSave={(nuevaOferta) => {
                    aplicarOfertaAScope(nuevaOferta);
                    setOfertaModal({ open: false });
                }}
                onDelete={() => {
                    aplicarOfertaAScope(null);
                    setOfertaModal({ open: false });
                }}
                titulo={ofertaModal.titulo || 'Aplicar oferta'}
                precioBase={ofertaModal.precioBase || 0}
                moneda={cotizacion.moneda}
                ofertaInicial={ofertaModal.oferta || null}
            />

            {/* Modal "perdiste el control": otro admin forzo la toma de tu lock.
                El hook ya corto todos los timers, aca solo avisamos + navegamos.
                Bloqueante — el user debe elegir a donde ir. */}
            <Modal
                open={!!lockPerdido}
                onClose={() => {}}
                closeOnOverlay={false}
                showClose={false}
                title="Perdiste el control de la cotización"
                size="sm"
                footer={
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/cotizaciones/mis-cotizaciones')}
                        >
                            Volver al listado
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/cotizaciones/${cotizacionId}/detalle`, { replace: true })}
                        >
                            Ver como espectador
                        </Button>
                    </>
                }
            >
                <p style={{ marginBottom: 8 }}>
                    <strong>{lockPerdido?.usuario_nombre || 'Otro usuario'}</strong> tomó el control de esta cotización.
                </p>
                <p className="text-sm text-muted">
                    Los cambios sin guardar se perdieron. Puedes volver al listado o ver la cotización como espectador.
                </p>
            </Modal>
        </form>
    );
};

export default CotizacionForm;
